import { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Megaphone, Info, ClipboardCheck, MessageCircle } from 'lucide-react';

// Stores
import { useLectureStore } from '../stores/lectureStore';
import { useChatStore } from '../stores/chatStore';
import { useTestStore } from '../stores/testStore';

// Hooks
import { useRealtimeChat } from '../hooks/useRealtimeChat';
import { useRealtimeQuestion } from '../hooks/useRealtimeQuestion';
import { useRealtimeLectureStatus } from '../hooks/useRealtimeLectureStatus';
import { useManabunAuth } from '../hooks/useManabunAuth';

// Utils

// API
import { fetchCurrentLectureStatus } from '../api/lectureStatus';
import { ensureFrontendSettings, getCachedFrontendSettings } from '../api/frontendSettings';
import { fetchAggregations, fetchEndedQuestions, fetchCurrentQuestion, checkQuestionAnswerable, mapBackendStatus, mapBackendQuestion, fetchAnswerHistory, fetchUserRanking } from '../api/realtimeQuestion';
import { saveQuestionsToLocalStorage } from '../api/s3DataFetcher';
import { serverNow } from '../utils/serverTime';
import { MAX_BACKOFF_EXPONENT, backoffDelayMs } from '../utils/wsBackoff';
import { loadTestData, cleanupExpiredLectureData } from '../utils/localStorage';

// Components
import { IVSPlayer, type IVSPlayerHandle } from '../components/VideoPlayer';
import { TestQuestion, TestSummary } from '../components/Test';
import { ChatInput, ChatMessages } from '../components/Chat';

// Types
import type { Question, TestResult as TestResultType } from '../types/test';
import { getMaterialDistributionLabel, type LectureMessage } from '../types/lecture';

// Logo
import logoImg from '../../../assets/data/logo_manabun_wide.png';

/**
 * リアルタイムチャットが利用可能かどうかを判定する。
 * チャット関連の環境変数がすべて設定されている場合に true を返す。
 */
/** リッチテキスト HTML からタグを除いたうえで、表示可能な文字があるか判定する */
function stripHtmlForMessageCheck(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function hasVisibleLectureMessage(
  message: LectureMessage | null | undefined,
  isArchive: boolean,
): boolean {
  if (isArchive || !message?.is_active) return false;
  const title = message.title?.trim() ?? '';
  const contents = stripHtmlForMessageCheck(message.contents ?? '');
  return !!(title || contents);
}

function isRealtimeChatAvailable(): boolean {
  const settings = getCachedFrontendSettings();
  return !!(settings?.chatRealtimeEndpoint && settings?.chatApiKey && settings?.chatHistoryApiEndpoint);
}

// ------------------------------------------------------------------
// 一斉アクセス分散用ジッタ（S7 / STU-H4対応）
// 講義終了イベントは全受講生に同時配信されるため、
// そのままでは全クライアントが同時刻に API 再フェッチを行い
// サーバー負荷スパイク（thundering herd）が発生する。ランダムな遅延で分散させる。
// ジッタ幅は 1講義500人 × 同時開催最大3講義（=1500人が同時刻に終了し得る）を前提に設定：
// - API取得: 1500人 ÷ 3秒 ≒ ピーク500リクエスト/秒（API Gateway・Lambda のクォータに対し十分余裕）
// ------------------------------------------------------------------
/** 講義終了（ended）受信直後の /questions/ended・/aggregations 取得を分散する最大遅延（ms） */
const ENDED_FETCH_JITTER_MAX_MS = 3_000;

/** 講義終了（actual_ended_at）後もチャット投稿を受け付ける猶予時間（ms） */
const CHAT_POST_GRACE_MS = 10 * 60 * 1000;

/**
 * リアルタイムテストが利用可能かどうかを判定する。
 * テスト関連の設定（管理画面のAppSync設定）がすべて登録されている場合に true を返す。
 */
function isRealtimeQuestionAvailable(): boolean {
  const settings = getCachedFrontendSettings();
  return !!(settings?.questionRealtimeEndpoint && settings?.questionApiKey);
}

/** EQ プレイヤーを video-wrapper の角丸に追従させる（body 直下配置のため親の overflow クリップが効かない） */
function syncEqPlayerBorderRadius(playerDiv: HTMLDivElement, wrapper: HTMLElement): void {
  const { borderRadius } = window.getComputedStyle(wrapper);
  if (playerDiv.style.borderRadius !== borderRadius) {
    playerDiv.style.borderRadius = borderRadius;
  }
}

/**
 * シアターモード・SP 固定シェルは内部/固定レイアウトのため fixed 座標を使う。
 * PC 通常表示は window スクロールに合わせて absolute + scroll オフセット。
 */
function isFixedEqPlayerPosition(): boolean {
  const body = document.body;
  return (
    body.classList.contains('viewer') &&
    (body.getAttribute('data-mode') === 'theater' ||
      body.getAttribute('data-sp-fixed-shell') === '1')
  );
}

/** SP 固定シェル（ヘッダー表示時）の下端。横シアターはヘッダー非表示のため null */
function getSpVisibleHeaderBottom(): number | null {
  const body = document.body;
  if (
    !body.classList.contains('viewer') ||
    body.getAttribute('data-sp-fixed-shell') !== '1' ||
    body.getAttribute('data-sp-theater-layout') === 'landscape'
  ) {
    return null;
  }
  const header = document.querySelector('.header');
  if (!(header instanceof HTMLElement) || getComputedStyle(header).display === 'none') {
    return null;
  }
  const headerRect = header.getBoundingClientRect();
  if (headerRect.height <= 0) return null;
  return headerRect.bottom;
}

function applyEqPlayerRect(playerDiv: HTMLDivElement, target: HTMLElement): boolean {
  const rect = target.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;

  const useFixed = isFixedEqPlayerPosition();
  let top = useFixed ? rect.top : rect.top + window.scrollY;
  const left = useFixed ? rect.left : rect.left + window.scrollX;
  const width = rect.width;
  let height = rect.height;

  const headerBottom = getSpVisibleHeaderBottom();
  if (useFixed && headerBottom !== null && rect.top < headerBottom - 0.5) {
    top = headerBottom;
    height = Math.max(0, rect.height - (headerBottom - rect.top));
  }

  playerDiv.style.position = useFixed ? 'fixed' : 'absolute';
  playerDiv.style.top = `${top}px`;
  playerDiv.style.left = `${left}px`;
  playerDiv.style.width = `${width}px`;
  playerDiv.style.height = `${height}px`;
  syncEqPlayerBorderRadius(playerDiv, target);
  syncTheaterHeaderClip(playerDiv, rect);
  syncSpTheaterToggleButton(target);
  return true;
}

/** SP アーカイブ: #eqplayer より前面にシアター切替ボタンを固定配置 */
function syncSpTheaterToggleButton(anchor: HTMLElement): void {
  const btn = document.getElementById('sp-theater-toggle-btn-sp');
  if (!btn) return;

  const isArchivePage = document.body.getAttribute('data-page') === 'archive';
  const isSpLayout = window.matchMedia('(max-width: 1023px)').matches;
  if (!isArchivePage || !isSpLayout) {
    btn.style.position = '';
    btn.style.zIndex = '';
    btn.style.top = '';
    btn.style.left = '';
    return;
  }

  const rect = anchor.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const headerBottom = getSpVisibleHeaderBottom();
  let top = rect.top + 8;
  if (headerBottom !== null && top < headerBottom + 8) {
    top = headerBottom + 8;
  }

  btn.style.position = 'fixed';
  btn.style.zIndex = '2700';
  btn.style.top = `${top}px`;
  btn.style.left = `${rect.left + 10}px`;
}

/** シアターヘッダーと重なる EQ 上部を clip し、ヘッダーが前面に見えるようにする */
function syncTheaterHeaderClip(playerDiv: HTMLDivElement, videoRect: DOMRect): void {
  if (
    document.body.getAttribute('data-mode') !== 'theater' ||
    document.body.getAttribute('data-sp-theater-layout') === 'landscape'
  ) {
    playerDiv.style.clipPath = '';
    return;
  }

  const header = document.getElementById('theater-lecture-title-header');
  if (!header || header.getAttribute('aria-hidden') === 'true') {
    playerDiv.style.clipPath = '';
    return;
  }

  const headerRect = header.getBoundingClientRect();
  if (headerRect.height <= 0) {
    playerDiv.style.clipPath = '';
    return;
  }

  const overlapsHorizontally =
    videoRect.left < headerRect.right && videoRect.right > headerRect.left;
  const overlapTop = headerRect.bottom - videoRect.top;

  if (!overlapsHorizontally || overlapTop <= 0) {
    playerDiv.style.clipPath = '';
    return;
  }

  const clipTop = Math.min(overlapTop, videoRect.height);
  playerDiv.style.clipPath = `inset(${clipTop}px 0 0 0)`;
}

function findEqTheaterScrollContainer(anchor: HTMLElement): HTMLElement | null {
  const mainCol = anchor.closest('.viewer-main-col');
  if (mainCol instanceof HTMLElement) {
    const { overflowY } = window.getComputedStyle(mainCol);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return mainCol;
    }
  }
  return null;
}

function canScrollElement(el: HTMLElement, deltaY: number): boolean {
  if (deltaY > 0) {
    return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
  }
  if (deltaY < 0) {
    return el.scrollTop > 0;
  }
  return false;
}

/** シアターモード: iframe 上のホイール/タッチを内部スクロールコンテナへ転送 */
function setupEqPlayerScrollOverlay(
  playerDiv: HTMLDivElement,
  getVideoWrapper: () => HTMLElement | null,
): () => void {
  playerDiv.querySelector('.eqplayer-scroll-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'eqplayer-scroll-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  playerDiv.appendChild(overlay);

  let touchStartY = 0;

  const forwardScroll = (deltaY: number, preventDefault: () => void): boolean => {
    if (document.body.getAttribute('data-mode') !== 'theater') return false;
    const anchor = getVideoWrapper();
    if (!anchor) return false;
    const scrollContainer = findEqTheaterScrollContainer(anchor);
    if (!scrollContainer || !canScrollElement(scrollContainer, deltaY)) return false;
    scrollContainer.scrollTop += deltaY;
    preventDefault();
    return true;
  };

  const onWheel = (e: WheelEvent) => {
    forwardScroll(e.deltaY, () => e.preventDefault());
  };

  const onTouchStart = (e: TouchEvent) => {
    touchStartY = e.touches[0]?.clientY ?? 0;
  };

  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    const deltaY = touchStartY - touch.clientY;
    touchStartY = touch.clientY;
    forwardScroll(deltaY, () => e.preventDefault());
  };

  const onMouseDown = () => {
    overlay.style.pointerEvents = 'none';
  };

  const onMouseUp = () => {
    overlay.style.pointerEvents = 'auto';
  };

  overlay.addEventListener('wheel', onWheel, { passive: false });
  overlay.addEventListener('touchstart', onTouchStart, { passive: true });
  overlay.addEventListener('touchmove', onTouchMove, { passive: false });
  overlay.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mouseup', onMouseUp);

  return () => {
    overlay.removeEventListener('wheel', onWheel);
    overlay.removeEventListener('touchstart', onTouchStart);
    overlay.removeEventListener('touchmove', onTouchMove);
    overlay.removeEventListener('mousedown', onMouseDown);
    document.removeEventListener('mouseup', onMouseUp);
    overlay.remove();
  };
}

// Side tabs type
type SideTab = 'side-chat' | 'side-chapters';
// SP tabs type
type SpTab = 'overview' | 'chat' | 'test' | 'chapters';

/**
 * LecturePage - 統合された講義視聴ページ
 * lecture.jsonのis_archive_publishedフラグでライブ/アーカイブを切り替え
 */
function LecturePage() {
  const { tenantSlug, internalId } = useParams<{
    tenantSlug: string;
    internalId: string;
  }>();

  // 保持期間を過ぎた過去講義の localStorage データを削除する（無期限蓄積の防止。STU-12対応）
  useEffect(() => {
    cleanupExpiredLectureData();
  }, []);

  // Manabun認証
  const { user: manabunUser, status: authStatus, error: authError, userType, canAccessCourse, isTrial } = useManabunAuth(tenantSlug);

  useEffect(() => {
    if (authStatus === 'authenticated' && manabunUser) {
      console.log('[LecturePage] Manabun認証成功:', {
        userId: manabunUser.USER.USER_ID,
        nickname: manabunUser.USER.USER_NICKNAME,
        userType,
      });
    } else if (authStatus === 'error' && authError) {
      console.error('[LecturePage] Manabun認証エラー:', authError);
    }
  }, [authStatus, manabunUser, authError, userType]);

  // ユーザーID: Manabun認証のAPIレスポンスから取得
  const currentUserId = useMemo(() => {
    if (manabunUser) {
      return String(manabunUser.USER.USER_ID);
    }
    
    // Manabun認証が完了していない場合は空文字を返す（認証完了後に再計算される）
    return '';
  }, [manabunUser]);
  const currentUserName = manabunUser?.USER.USER_NICKNAME ?? '受講者';

  // Stores
  const {
    lectureData,
    noticeData,
    questionsData,
    ngUsers,
    ngWords,
    error,
    fetchAllData,
    clearError,
    upsertQuestion,
    replaceQuestions,
    updateQuestionStatus,
    questionAggregations,
    setQuestionAggregation,
    setLectureStatus,
    userRanking,
    setUserRanking,
  } = useLectureStore();

  const {
    messages: localMessages,
    isInputEnabled,
    validateAndCheck,
    submitMessage,
    setInputEnabled,
  } = useChatStore();

  const {
    selectChoice,
    selectedChoices,
    stopTimer,
    submitAnswer,
    submitUnanswered,
    startAnswerTimer,
    setRealtimeSubmitFn,
    setCheckAnswerableFn,
    setFetchServerAnswerFn,
    setUserId,
    setQuestionNumMap,
    setLectureId: setTestLectureId,
    submittedQuestions,
    answerSendStates,
    setQuestionDeadline,
    resetQuestion,
    questionNumMap,
    displayNumberMap,
    errorMessage,
    setErrorMessage,
    userId,
    reconcileAnswersWithServer,
    restoreAnswerFromLocalStorage,
    restoreAnswerStartTime,
    setQuestionChoices,
  } = useTestStore();

  // Derived state（useRealtimeChat より前に定義が必要）
  const isArchive = lectureData?.archive.is_archive_published ?? false;
  const hasMessage = useMemo(
    () => hasVisibleLectureMessage(lectureData?.message, isArchive),
    [lectureData?.message, isArchive],
  );

  // URL の internalId をそのまま CDK API / AppSync に使用する
  const lectureIdStr = lectureData?.internal_id ?? internalId ?? null;

  // 試用ユーザー判定: 現在の講座ID（lectureData.course.id）に対して個別に判定する
  const isTrialUser = useMemo(() => {
    if (!manabunUser) return false;
    // staffは試用扱いしない
    if (userType === 'staff') return false;
    // lectureDataの講座IDが取得できた場合は講座単位で判定
    if (lectureData?.course?.id !== undefined) {
      return isTrial(lectureData.course.id);
    }
    // lectureDataがまだ読み込まれていない場合はfalseを返す
    return false;
  }, [manabunUser, userType, lectureData, isTrial]);

  // 視聴フロントエンド設定（frontend-settings API）を先行取得し、完了後に各機能の可用性を判定する。
  // 取得失敗時はバックオフ付きで自動リトライする（STU-H6対応）。
  // 旧実装は失敗しても loaded=true にして打ち切っており、一時的なネットワーク断で
  // チャット・テスト機能がセッション中ずっと無効のままになっていた。
  const [frontendSettingsLoaded, setFrontendSettingsLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const load = () => {
      ensureFrontendSettings()
        .then(() => {
          if (!cancelled) setFrontendSettingsLoaded(true);
        })
        .catch((e) => {
          console.error('[LecturePage] 視聴設定の取得に失敗:', e);
          if (cancelled) return;
          attempt = Math.min(attempt + 1, MAX_BACKOFF_EXPONENT);
          const delay = backoffDelayMs(attempt);
          console.log(`[LecturePage] 視聴設定の取得を ${delay}ms 後に再試行します (試行 ${attempt})`);
          retryTimer = setTimeout(load, delay);
        });
    };
    load();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Manabunへ戻るリンク（外部ドメイン設定から生成。未取得の間は無効リンク）
  const manabunDomain = frontendSettingsLoaded ? getCachedFrontendSettings()?.manabunDomain : '';
  const manabunBackUrl = manabunDomain ? `https://${manabunDomain}/realtime-class` : '#';

  // リアルタイムチャット: 設定（管理画面のAppSync設定）が登録されていれば有効化
  const useRealtime = frontendSettingsLoaded && isRealtimeChatAvailable();
  const {
    messages: realtimeMessages,
    isConnected: isChatWsConnected,
    isLoading: isChatHistoryLoading,
    error: chatConnectionError,
    sendMessage: sendRealtimeMessage,
  } = useRealtimeChat({
    tenantId: tenantSlug || '',
    lectureId: lectureIdStr ?? '',
    currentUserId: currentUserId || '',
    // 履歴取得はアーカイブ視聴でも行う（過去ログの閲覧のみ）
    enabled: useRealtime && !!tenantSlug && !!lectureIdStr && !!currentUserId && authStatus === 'authenticated',
    // WebSocket接続は lectureData 読込でアーカイブでないことが確定してから開始する
    // （読込前は isArchive=false のため、アーカイブでも一瞬接続→切断が発生していた）
    connectEnabled: !!lectureData && !isArchive,
  });

  // ------------------------------------------------------------------
  // リアルタイムテスト: Question Backend CDK への WebSocket 接続
  // ------------------------------------------------------------------
  const useRealtimeTest = frontendSettingsLoaded && isRealtimeQuestionAvailable();

  // 問題API初期化完了フラグ（WebSocket接続前にAPI復元を完了させる）
  const [isQuestionApiReady, setIsQuestionApiReady] = useState(false);

  // 最後に問題WSイベントを処理した時刻（再同期結果の鮮度判定に使用）
  const lastQuestionEventAtRef = useRef(0);
  // syncCurrentQuestion の多重実行ガード
  const syncInFlightRef = useRef(false);
  // syncCurrentQuestion への参照（定義より前のハンドラから呼び出すため）
  const syncCurrentQuestionRef = useRef<(() => void) | null>(null);

  const handleQuestionOpened = useCallback(
    (event: {
      questionId: number;
      displayNumber: number | null;
      question: import('../types/test').Question;
      scheduledCloseTime: number;
      aggregation?: { choiceCounts: Record<string, number>; totalAnswers: number; correctCount: number };
    }) => {
      lastQuestionEventAtRef.current = Date.now();
      console.log('[LecturePage][handleQuestionOpened] 呼び出し:', { questionId: event.questionId, displayNumber: event.displayNumber, status: event.question.status, scheduledCloseTime: event.scheduledCloseTime });

      // 同一出題セッションの重複イベント判定（STU-M2対応）
      // AppSync の再配信・再接続などで同じ QuestionOpened が重複到達した場合、
      // 無条件に resetQuestion すると送信済みの回答が消えてしまう。
      // 締切時刻が既知の値と一致する（=同じ出題セッション）ならリセットをスキップする。
      // 中止→再出題では締切時刻が必ず変わるため、従来どおりリセットされる。
      const isSameOpenSession =
        event.scheduledCloseTime > 0 &&
        scheduledCloseTimesRef.current[event.question.id] === event.scheduledCloseTime;

      // 新規出題の場合は常にリセット（管理画面で中止→再出題に対応）
      // キャッチアップ復元（aggregationあり）・同一セッションの重複イベントの場合は
      // リセットしない（既存の回答データを保護）
      // リセットは締切保存より先に実行する（resetQuestion は questionDeadlines /
      // questionTimeLimits も削除するため、締切保存後に実行すると保存した締切が
      // 直後に消え、回答送信リトライの締切打ち切り防御層が無効化される。STU-4対応）
      if (event.question.status === 'answering' && !event.aggregation && !isSameOpenSession) {
        console.log('[LecturePage][handleQuestionOpened] 問題をリセット:', event.question.id);
        resetQuestion(event.question.id);
      }

      if (event.scheduledCloseTime > 0) {
        scheduledCloseTimesRef.current[event.question.id] = event.scheduledCloseTime;
        // 回答送信リトライの打ち切り判定用に締切時刻・制限時間をストアにも保存
        setQuestionDeadline(event.question.id, event.scheduledCloseTime, event.question.time_limit * 1000);
      }

      upsertQuestion(event.question);
      console.log('[LecturePage][handleQuestionOpened] upsertQuestion 完了');
      
      // displayNumber を questionId と紐付けて保存
      console.log('[LecturePage][handleQuestionOpened] displayNumber設定:', { 
        questionId: event.questionId, 
        displayNumber: event.displayNumber,
        format_type: event.question.format_type 
      });
      setQuestionNumMap(event.questionId, event.questionId, event.displayNumber ?? null);
      
      // 選択肢情報を保存（choice_internal_id送信用）
      const choicesInfo = event.question.choices.map(c => ({
        id: c.id,
        internal_id: c.internal_id,
      }));
      setQuestionChoices(event.questionId, choicesInfo);
      
      // キャッチアップ復元時に集計データが含まれている場合は保存
      if (event.aggregation) {
        setQuestionAggregation(event.question.id, event.aggregation);
      }
      startAnswerTimer(event.question.id);
    },
    [upsertQuestion, startAnswerTimer, setQuestionAggregation, resetQuestion, setQuestionNumMap, setQuestionChoices, setQuestionDeadline],
  );

  const handleQuestionClosed = useCallback(
    (event: {
      questionId: number;
      correctChoice: number;
      choices: { num: number; text: string; is_correct: boolean; description?: string }[];
      explanation: string;
      totalAnswers?: number;
      correctCount?: number;
      choiceCounts?: Record<string, number>;
      helpText?: string;
    }) => {
      lastQuestionEventAtRef.current = Date.now();
      const existingQ = questionsData?.questions.find(
        (q) => q.id === event.questionId,
      );
      if (existingQ) {
        // ローカルストレージから最終回答を読み込み、selectedChoicesとsubmittedQuestionsを復元
        // （選択しただけで送信していない状態を上書き防止）
        if (lectureIdStr && userId) {
          restoreAnswerFromLocalStorage(lectureIdStr, userId, existingQ.id);
        }

        const updatedChoices = existingQ.choices.map((c) => {
          const closedChoice = event.choices.find((cc) => cc.num === c.id);
          if (!closedChoice) return c;
          // 選択肢ごとの解説を優先し、なければ全体の explanation をフォールバック
          const desc = closedChoice.description || event.explanation;
          return { ...c, is_correct: closedChoice.is_correct, description: desc };
        });
        
        // help_textを更新（event.helpTextがあればそれを使用、なければexistingQの値を保持）
        const updatedQuestion = {
          ...existingQ,
          status: 'tallied' as const,
          choices: updatedChoices,
          help_text: event.helpText !== undefined ? event.helpText : existingQ.help_text,
        };
        
        upsertQuestion(updatedQuestion);

        // 回答分布データを保存（グラフ表示用）
        if (event.choiceCounts) {
          setQuestionAggregation(existingQ.id, {
            choiceCounts: event.choiceCounts,
            totalAnswers: event.totalAnswers ?? 0,
            correctCount: event.correctCount ?? 0,
          });
        }
      } else {
        // 途中参加などで QuestionOpened を受信しておらずローカルに問題が存在しない場合、
        // イベントを破棄せず /questions/current から最新状態を再同期する（LC-M3対応）
        console.log(
          '[LecturePage][handleQuestionClosed] ローカルに問題が存在しないため再同期:',
          event.questionId,
        );
        syncCurrentQuestionRef.current?.();
      }
    },
    [questionsData, upsertQuestion, setQuestionAggregation, lectureIdStr, userId, restoreAnswerFromLocalStorage],
  );

  const handleExplanationEnded = useCallback(
    (questionId: number) => {
      lastQuestionEventAtRef.current = Date.now();
      stopTimer();
      
      // 既に回答済みの場合は回答データを削除しない（データを保護）
      if (!submittedQuestions.has(questionId)) {
        resetQuestion(questionId);
      }
      
      updateQuestionStatus(questionId, 'completed');
    },
    [stopTimer, resetQuestion, updateQuestionStatus, submittedQuestions],
  );

  const handleTestCancelled = useCallback(
    (questionId: number) => {
      lastQuestionEventAtRef.current = Date.now();
      stopTimer();

      // 中止時はサーバー側（cancelTest）で該当問題の回答・履歴が全削除されるため、
      // 回答済みでもクライアント側の送信状態・選択・localStorage を必ずクリアする（L8対応）。
      // 残したままだと再出題→リロード時に削除済みの旧回答が「送信済み」として復元され、
      // 再回答できなくなる。
      resetQuestion(questionId);

      updateQuestionStatus(questionId, 'not_started');
    },
    [stopTimer, resetQuestion, updateQuestionStatus],
  );

  // ------------------------------------------------------------------
  // 現在の問題の同期（リロード時の初回復元＋WebSocket再接続時の再同期）
  // /questions/current APIで現在進行中または集計済みの問題を取得して復元する。
  // APIのレスポンスを最優先し、ローカルストレージの内容よりも優先する。
  // 冪等に作られており、購読成功（初回・再接続）のたびに実行しても安全。
  // ------------------------------------------------------------------
  const syncCurrentQuestion = useCallback(async () => {
    if (!tenantSlug || !lectureIdStr || isArchive) return;
    // 多重実行ガード
    if (syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    const fetchStartedAt = Date.now();

    // 回答の復元（サーバーが正）:
    // 切断中の「中止（回答リセット）→再出題」を跨いでいる可能性があるため、
    // localStorage の回答を復元する前にサーバーの回答履歴と照合する。
    // サーバーに回答が存在しなければ中止で削除された旧回答とみなしローカルもクリアする。
    const restoreAnswerWithServerCheck = async (questionId: number): Promise<void> => {
      if (!lectureIdStr || !userId) return;
      // localStorage に回答がなければ復元・照合とも不要
      const hasLocalAnswer = loadTestData(lectureIdStr, userId).answers.some(
        (a) => a.questionId === questionId,
      );
      if (!hasLocalAnswer) return;

      const history = await fetchAnswerHistory(tenantSlug, userId, lectureIdStr);
      // 履歴取得中に WS イベントを処理していた場合はこの復元結果を破棄する
      if (lastQuestionEventAtRef.current > fetchStartedAt) {
        console.log('[LecturePage][Sync] 回答履歴取得中にWSイベントを処理したため復元を破棄');
        return;
      }
      if (history === null) {
        // 判定不能（APIエラー等）の場合は従来どおり localStorage から復元する
        restoreAnswerFromLocalStorage(lectureIdStr, userId, questionId);
      } else if (history.answers.some((a) => a.question_id === questionId)) {
        restoreAnswerFromLocalStorage(lectureIdStr, userId, questionId);
      } else {
        // サーバーに回答が存在しない → 中止で削除された旧回答とみなしローカルもクリアする
        console.log('[LecturePage][Sync] サーバーに回答が存在しないためローカルの旧回答をクリア:', questionId);
        resetQuestion(questionId);
      }
    };

    try {
      const result = await fetchCurrentQuestion(tenantSlug, lectureIdStr);

      // fetch中にWSイベントを処理していた場合、このレスポンスは古いスナップショットの
      // 可能性があるため破棄する（WSイベント側が既に新しい状態を反映済み）
      if (lastQuestionEventAtRef.current > fetchStartedAt) {
        console.log('[LecturePage][Sync] fetch中にWSイベントを処理したため再同期結果を破棄');
        return;
      }

      // APIで現在の問題が取得できない場合、進行中の問題をすべてリセット
      if (!result?.current_question) {
        // ストアに残っている進行中の問題をすべて completed に更新
        const currentQuestions = questionsData?.questions ?? [];
        currentQuestions.forEach((q) => {
          if (q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied') {
            updateQuestionStatus(q.id, 'completed');
          }
        });

        // 締切予定時刻もクリア
        scheduledCloseTimesRef.current = {};
        return;
      }

      const { question_id, display_number, status, question, aggregation } = result.current_question;
      const frontStatus = mapBackendStatus(status) as Question['status'];

      const mappedQuestion = mapBackendQuestion(question, question_id, frontStatus, false, display_number);

      // 選択肢情報を保存（choice_internal_id送信用）
      const choicesInfo = mappedQuestion.choices.map(c => ({
        id: c.id,
        internal_id: c.internal_id,
      }));
      setQuestionChoices(question_id, choicesInfo);

      // APIのレスポンスがcompletedの場合、この問題と他の進行中の問題をすべてcompletedに更新
      if (frontStatus === 'completed') {
        // 取得した問題を completed で保存
        upsertQuestion(mappedQuestion);
        setQuestionNumMap(mappedQuestion.id, question_id, display_number ?? null);

        // サーバーの回答履歴と照合したうえで回答を復元（正しい回答を表示するため）
        await restoreAnswerWithServerCheck(mappedQuestion.id);

        // ストアに残っている他の進行中の問題もすべて completed に更新
        const currentQuestions = questionsData?.questions ?? [];
        currentQuestions.forEach((q) => {
          if (q.id !== mappedQuestion.id &&
              (q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied')) {
            updateQuestionStatus(q.id, 'completed');
          }
        });

        // 締切予定時刻もクリア
        scheduledCloseTimesRef.current = {};
        return;
      }

      // 問題を復元（answering or tallied の場合）
      upsertQuestion(mappedQuestion);
      setQuestionNumMap(mappedQuestion.id, question_id, display_number ?? null);

      // 回答の復元（受講生B-3対応・サーバーが正）:
      // 切断中の「中止→再出題」を跨いでいる可能性があるため、ステータスを問わず
      // サーバーの回答履歴で回答の有無を確認してから localStorage の回答を復元する。
      // （answering: 旧回答を「送信済み」として復元すると再回答できなくなる、
      //   tallied: 中止で削除済みの旧回答が正解として復活表示されてしまう）
      await restoreAnswerWithServerCheck(mappedQuestion.id);

      // scheduled_close_time を保存（タイマー残り時間計算用）
      // answering状態でなくても、scheduled_close_timeがあれば保存する（タイマー復元のため）
      // 注: 上の resetQuestion で締切時刻もクリアされ得るため、この順序（復元判定→締切保存）を維持すること
      if (aggregation?.scheduled_close_time) {
        scheduledCloseTimesRef.current[mappedQuestion.id] = aggregation.scheduled_close_time;
        // 回答送信リトライの打ち切り判定用に締切時刻・制限時間をストアにも保存
        setQuestionDeadline(mappedQuestion.id, aggregation.scheduled_close_time, mappedQuestion.time_limit * 1000);
      } else {
        // scheduled_close_timeがない場合はクリア（talliedの場合など）
        delete scheduledCloseTimesRef.current[mappedQuestion.id];
      }

      // answering（出題中）の復元では QuestionOpened を経由しないため、回答開始時刻を
      // 出題時刻（opened_at）から逆算して復元する（受講生B-2対応）。
      // これがないと answer_time_ms=0 で送信され、回答時間ランキングで最速扱いになってしまう。
      if (frontStatus === 'answering') {
        // answerStartTimes は Date.now() 基準のため、サーバー時刻オフセットを補正して端末時刻に変換する
        const serverOffsetMs = serverNow() - Date.now();
        const openedAtLocal = aggregation?.opened_at
          ? aggregation.opened_at - serverOffsetMs
          : Date.now();
        restoreAnswerStartTime(mappedQuestion.id, openedAtLocal);
      }

      // 集計データがあれば復元
      if (aggregation && (frontStatus === 'tallied' || (frontStatus as any) === 'completed')) {
        setQuestionAggregation(mappedQuestion.id, {
          choiceCounts: aggregation.choice_counts ?? {},
          totalAnswers: aggregation.total_answers ?? 0,
          correctCount: aggregation.correct_count ?? 0,
        });
      }

      // APIで取得した問題以外の進行中の問題を completed に更新
      // （現在の進捗外の問題は表示しない。切断中に QuestionClosed/ExplanationEnded を
      //   取りこぼした場合の復旧にも必須）
      const currentQuestions = questionsData?.questions ?? [];
      currentQuestions.forEach((q) => {
        if (q.id !== mappedQuestion.id &&
            (q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied')) {
          console.log(`[LecturePage][Sync] 進捗外の問題を完了扱いに更新 question.id:${q.id} (${q.status} -> completed)`);
          updateQuestionStatus(q.id, 'completed');
          // 締切予定時刻もクリア
          delete scheduledCloseTimesRef.current[q.id];
        }
      });
    } catch (error) {
      console.warn('[LecturePage][Sync] 現在の問題の同期エラー:', error);
    } finally {
      // 初回復元の完了をマークし、WebSocket接続を許可（再同期時は実質no-op）
      // エラー時もWebSocket接続を許可（接続による復旧を試みる）
      hasRestoredQuestionRef.current = true;
      setIsQuestionApiReady(true);
      syncInFlightRef.current = false;
    }
  }, [tenantSlug, lectureIdStr, isArchive, questionsData, upsertQuestion, setQuestionNumMap, setQuestionAggregation, updateQuestionStatus, userId, restoreAnswerFromLocalStorage, restoreAnswerStartTime, resetQuestion, setQuestionChoices, setQuestionDeadline]);

  // 定義位置より前のハンドラ（handleQuestionClosed 等）から呼び出せるよう ref に保持する
  useEffect(() => {
    syncCurrentQuestionRef.current = syncCurrentQuestion;
  }, [syncCurrentQuestion]);

  // WebSocket購読成功時（初回・再接続とも）に現在の問題を再同期する
  // （切断中に配信された QuestionOpened/QuestionClosed 等の取りこぼしを回復する）
  const handleQuestionSubscribeSuccess = useCallback(() => {
    console.log('[LecturePage] 問題チャンネル購読成功 → 現在の問題を再同期');
    syncCurrentQuestion();
  }, [syncCurrentQuestion]);

  // デバッグログ追加
  useEffect(() => {
    console.log('[LecturePage][DEBUG] useRealtimeQuestion enabled条件:', {
      useRealtimeTest,
      isArchive,
      lectureDataLoaded: !!lectureData,
      tenantSlug,
      lectureIdStr,
      isQuestionApiReady,
      currentUserId,
      authStatus,
      enabled: useRealtimeTest && !!lectureData && !isArchive && !!tenantSlug && !!lectureIdStr && isQuestionApiReady && !!currentUserId && authStatus === 'authenticated',
    });
  }, [useRealtimeTest, isArchive, lectureData, tenantSlug, lectureIdStr, isQuestionApiReady, currentUserId, authStatus]);

  const {
    isConnected: isQuestionWsConnected,
    submitAnswer: realtimeSubmitAnswer,
  } = useRealtimeQuestion({
    tenantId: tenantSlug || '',
    lectureId: lectureIdStr ?? '',
    // lectureData 読込済み（＝アーカイブでないことが確定）かつ currentUserIdが存在し認証成功時のみ有効化
    enabled: useRealtimeTest && !!lectureData && !isArchive && !!tenantSlug && !!lectureIdStr && isQuestionApiReady && !!currentUserId && authStatus === 'authenticated',
    userId: currentUserId,
    onQuestionOpened: handleQuestionOpened,
    onQuestionClosed: handleQuestionClosed,
    onExplanationEnded: handleExplanationEnded,
    onTestCancelled: handleTestCancelled,
    onSubscribeSuccess: handleQuestionSubscribeSuccess,
    onError: useCallback((error: Error) => {
      console.error('[LecturePage][onError]', error.message);
      // 接続系エラー（WebSocket切断・再接続中の設定取得失敗）は全画面モーダルを表示しない（STU-7対応）。
      // 切断状態はヘッダーの接続バッジで通知済みで、切断中は再接続試行のたびに onError が
      // 繰り返し呼ばれるため、モーダルを出すと視聴の妨げになる。
      // 業務エラー（回答拒否等の日本語メッセージ）のみモーダルで表示する
      if (error.name === 'ConnectionError' || /websocket/i.test(error.message)) return;
      setErrorMessage(error.message);
    }, [setErrorMessage]),
  });

  // ------------------------------------------------------------------
  // 講義終了/アーカイブ時の全問題データ補完（/questions/ended）
  // リアルタイム受信・リロード・アーカイブ・再同期のすべての契機から共通で使用する。
  // - endedQuestionsFetchedRef は「取得・反映に成功した後」にのみ true にする。
  //   失敗（403/ネットワークエラー/空レスポンス）時は false のままにし、
  //   次の契機（lectureData 更新・ended 再受信・再同期）で再試行できるようにする。
  // - 多重実行は endedQuestionsFetchInFlightRef で防止する。
  // - 講義がやり直された（ended → ready/running）場合はフラグをリセットし、
  //   次回の講義終了で再取得できるようにする（講義終了画面集計問題対応）。
  // ------------------------------------------------------------------
  const endedQuestionsFetchedRef = useRef(false);
  const endedQuestionsFetchInFlightRef = useRef(false);

  // ------------------------------------------------------------------
  // 集計データ（/aggregations）の一括取得・反映（STU-H4対応）
  // fetchAndApplyEndedQuestions と講義終了時の useEffect の2箇所から呼ばれるため、
  // 取得済み・実行中ガードを共通化して同一データの二重フェッチを防ぐ。
  // aggregationFetchedRef は取得・反映に成功した後にのみ true にし、
  // 失敗時は次の契機で再試行できるようにする。
  // ------------------------------------------------------------------
  const aggregationFetchedRef = useRef(false);
  const aggregationFetchInFlightRef = useRef(false);

  const fetchAndApplyAggregations = useCallback(async () => {
    if (!tenantSlug || !lectureIdStr) return;
    if (aggregationFetchedRef.current || aggregationFetchInFlightRef.current) return;
    aggregationFetchInFlightRef.current = true;
    try {
      const aggregationsResponse = await fetchAggregations(tenantSlug, lectureIdStr);
      if (aggregationsResponse && aggregationsResponse.aggregations) {
        aggregationsResponse.aggregations.forEach((agg) => {
          setQuestionAggregation(agg.question_id, {
            choiceCounts: agg.choice_counts,
            totalAnswers: agg.total_answers,
            correctCount: agg.correct_count,
          });
        });
        console.log('[LecturePage] 全問題の集計データ取得完了:', aggregationsResponse.aggregations.length, '問');
        aggregationFetchedRef.current = true;
      }
    } catch (error) {
      console.error('[LecturePage] /aggregations 取得エラー（次の契機で再試行します）:', error);
    } finally {
      aggregationFetchInFlightRef.current = false;
    }
  }, [tenantSlug, lectureIdStr, setQuestionAggregation]);

  const fetchAndApplyEndedQuestions = useCallback(
    async (options?: { jitter?: boolean }) => {
      if (!tenantSlug || !lectureIdStr) return;
      if (endedQuestionsFetchedRef.current || endedQuestionsFetchInFlightRef.current) return;
      endedQuestionsFetchInFlightRef.current = true;

      try {
        // ended は全受講生に同時配信されるため、直後の API 取得を
        // ランダムな遅延で分散させる（S7対応）
        if (options?.jitter) {
          const jitterMs = Math.floor(Math.random() * ENDED_FETCH_JITTER_MAX_MS);
          console.log(`[LecturePage] 一斉アクセス分散のため ${jitterMs}ms 待機`);
          await new Promise((resolve) => setTimeout(resolve, jitterMs));
        }

        console.log('[LecturePage] /questions/ended で全問題データを取得');
        const response = await fetchEndedQuestions(tenantSlug, lectureIdStr);

        if (!response || !response.questions || response.questions.length === 0) {
          console.warn('[LecturePage] /questions/ended からデータを取得できませんでした（次の契機で再試行します）');
          return;
        }

        console.log('[LecturePage] /questions/ended から問題データを取得:', response.questions.length, '問');

        // 全問題をフロントエンドの型に変換（講義終了後なので解説を強制表示）
        const mappedQuestions = response.questions.map((item) => {
          const questionId = item.question.question_id ?? item.question.id ?? 0;
          const backendStatus = mapBackendStatus(item.status);
          return mapBackendQuestion(item.question, questionId, backendStatus, true, item.display_number);
        });

        // 選択肢情報をすべて保存（choice_internal_id送信用）
        mappedQuestions.forEach((question) => {
          const choicesInfo = question.choices.map((c) => ({
            id: c.id,
            internal_id: c.internal_id,
          }));
          setQuestionChoices(question.id, choicesInfo);
        });

        // QuestionsData 形式に整形
        const questionsDataToSave: import('../types').QuestionsData = {
          meta: {
            generated_at: new Date().toISOString(),
            version: '1.0',
            tenant: { url_slug: tenantSlug },
            lecture: { id: 0, name: lectureData?.name || '' },
          },
          questions: mappedQuestions,
        };

        // localStorageに全更新で保存
        saveQuestionsToLocalStorage(tenantSlug, lectureIdStr, questionsDataToSave);

        // ストアに一括反映（upsert の逐次反映は並行する fetchAllData の全置換に弱いため一括置換する）
        replaceQuestions(questionsDataToSave);

        // questionNumMapとdisplayNumberMapを設定
        response.questions.forEach((item) => {
          const questionId = item.question.question_id || item.question.id;
          if (questionId) {
            setQuestionNumMap(questionId, questionId, item.display_number ?? null);
          }
        });

        // 全問題の反映に成功した時点でフラグを立てる（失敗時は再試行可能なまま）
        endedQuestionsFetchedRef.current = true;

        // 集計データを一括取得（パフォーマンス改善：N回 → 1回。
        // 取得済み・実行中ガードは共通関数内で行う。STU-H4対応）
        await fetchAndApplyAggregations();

        console.log('[LecturePage] /questions/ended からの問題データ補完完了');
      } catch (error) {
        console.error('[LecturePage] /questions/ended 取得エラー（次の契機で再試行します）:', error);
      } finally {
        endedQuestionsFetchInFlightRef.current = false;
      }
    },
    [tenantSlug, lectureIdStr, lectureData?.name, replaceQuestions, setQuestionChoices, setQuestionNumMap, fetchAndApplyAggregations],
  );

  // ------------------------------------------------------------------
  // 講義ステータス: リアルタイム受信
  // 管理画面が講義終了を publish → ended を受け取りストアを更新 → TestSummary 表示
  // ------------------------------------------------------------------
  const handleLectureStatusChange = useCallback(
    async (status: 'ready' | 'running' | 'ended') => {
      console.log('[LecturePage] 講義ステータス変更:', status);
      setLectureStatus(status);

      if (status !== 'ended') {
        // 講義がやり直された（ended → ready/running）場合に備えて
        // 講義終了時の一回限り取得フラグをリセットし、次回の ended で再取得できるようにする
        endedQuestionsFetchedRef.current = false;
        aggregationFetchedRef.current = false;
        return;
      }

      // ended 受信時は S3 の lecture.json を再フェッチして actual_ended_at を取得する。
      if (tenantSlug && internalId) {
        console.log('[LecturePage] ended 受信後 S3 再フェッチ');
        await fetchAllData(tenantSlug, internalId, false);
        setLectureStatus('ended');
      }

      // 講義終了時に /questions/ended から全問題データを取得
      // （useEffect 側で取得済み/実行中の場合は共通関数内のガードでスキップされる）
      console.log('[LecturePage] リアルタイム講義終了 → 全問題データを補完');
      await fetchAndApplyEndedQuestions({ jitter: true });
    },
    [setLectureStatus, fetchAllData, tenantSlug, internalId, fetchAndApplyEndedQuestions],
  );

  // 講義ステータスチャンネル購読成功時（初回・再接続とも）に最新ステータスを再同期する
  // （切断中に LectureStatusChanged（講義終了など）を取りこぼした場合の回復。
  //   ended の場合は useEffect 側のフラグ消費状態に依存せず、ここでも全問題補完を実行する）
  const handleLectureStatusSubscribeSuccess = useCallback(() => {
    if (isArchive || !tenantSlug || !internalId) return;
    console.log('[LecturePage] 講義ステータス購読成功 → 最新ステータスを再同期');
    fetchCurrentLectureStatus(tenantSlug, internalId)
      .then((status) => {
        if (!status) return;
        setLectureStatus(status);
        if (status === 'ended') {
          void fetchAndApplyEndedQuestions();
        } else {
          // 講義がやり直された場合に備えてフラグをリセット（次回の ended で再取得可能にする）
          endedQuestionsFetchedRef.current = false;
          aggregationFetchedRef.current = false;
        }
      })
      .catch((error) => {
        console.warn('[LecturePage] 講義ステータス再同期エラー:', error);
      });
  }, [isArchive, tenantSlug, internalId, setLectureStatus, fetchAndApplyEndedQuestions]);

  const { isConnected: isLectureStatusWsConnected } = useRealtimeLectureStatus({
    tenantId: tenantSlug || '',
    lectureId: lectureIdStr ?? '',
    // lectureData 読込済み（＝アーカイブでないことが確定）かつ currentUserIdが存在し認証成功時のみ有効化
    enabled: !!lectureData && !isArchive && !!tenantSlug && !!lectureIdStr && !!currentUserId && authStatus === 'authenticated',
    userId: currentUserId,
    onStatusChange: handleLectureStatusChange,
    onSubscribeSuccess: handleLectureStatusSubscribeSuccess,
  });

  // ------------------------------------------------------------------
  // リアルタイム接続状態のヘッダー表示
  // WebSocket切断が受講生に一切通知されない問題への対応（管理画面PERF-FE-C1と同等）。
  // 一度でも接続に成功した後に切断された場合のみバッジを表示する
  // （初回接続中の一時的な未接続状態でバッジが点滅するのを防ぐ）
  // ------------------------------------------------------------------
  const realtimeWsBase =
    !!lectureData && !isArchive && !!tenantSlug && !!lectureIdStr && !!currentUserId && authStatus === 'authenticated';
  const questionWsEnabled = useRealtimeTest && isQuestionApiReady && realtimeWsBase;
  // 講義ステータス接続は Question Backend の設定を使用するため useRealtimeTest でゲートする
  const lectureStatusWsEnabled = useRealtimeTest && realtimeWsBase;
  const chatWsEnabled = useRealtime && realtimeWsBase;
  const isRealtimeDisconnected =
    (questionWsEnabled && !isQuestionWsConnected) ||
    (lectureStatusWsEnabled && !isLectureStatusWsConnected) ||
    (chatWsEnabled && !isChatWsConnected);
  const [hasRealtimeConnectedOnce, setHasRealtimeConnectedOnce] = useState(false);
  useEffect(() => {
    if ((questionWsEnabled || lectureStatusWsEnabled || chatWsEnabled) && !isRealtimeDisconnected) {
      setHasRealtimeConnectedOnce(true);
    }
  }, [questionWsEnabled, lectureStatusWsEnabled, chatWsEnabled, isRealtimeDisconnected]);
  const showRealtimeDisconnectedBadge = hasRealtimeConnectedOnce && isRealtimeDisconnected;

  // testStore に lectureId を設定（回答送信で必要）
  // 講義ID変更時のストアリセットは本コンポーネント内の useLayoutEffect で行う（S3対応・受講生B-1）
  useEffect(() => {
    if (lectureIdStr) {
      setTestLectureId(lectureIdStr);
    }
  }, [lectureIdStr, setTestLectureId]);

  // WebSocket 受信・ステータス変更で更新された問題データを localStorage に保存し、
  // リロード時に fetchQuestions が復元できるようにする（STU-10対応）。
  // 以前は lectureStore 内で meta.lecture.id を基に保存していたが、WS 受信で構築される
  // questionsData の meta.lecture.id は常に 0 のため一度も保存されない実質デッドコードだった。
  // tenantSlug / lectureIdStr を確実に持つ本コンポーネントで保存する
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr) return;
    if (!questionsData || questionsData.questions.length === 0) return;
    saveQuestionsToLocalStorage(tenantSlug, lectureIdStr, questionsData);
  }, [tenantSlug, lectureIdStr, questionsData]);

  // WebSocket 回答送信関数を testStore に注入（回答送信はライブ・本受講ユーザーのみ）
  useEffect(() => {
    if (useRealtimeTest && !isArchive && !isTrialUser) {
      setRealtimeSubmitFn(realtimeSubmitAnswer);
    }
    return () => {
      setRealtimeSubmitFn(null);
    };
  }, [useRealtimeTest, isArchive, isTrialUser, realtimeSubmitAnswer, setRealtimeSubmitFn]);

  // ユーザーIDを testStore に注入する。
  // 回答履歴（/answers/history）・ランキング（/user-ranking）の復元でも userId を
  // 使用するため、アーカイブ視聴・試用ユーザーでも設定する（STU-3対応）。
  // 従来は回答送信関数の注入と同じ条件（!isArchive 等）でガードされており、
  // アーカイブ視聴で自分の回答・ランキングが一切復元されなかった
  useEffect(() => {
    if (currentUserId) {
      setUserId(currentUserId);
    }
  }, [currentUserId, setUserId]);

  // 回答受付状態のサーバー確認関数を testStore に注入
  // （送信リトライ再開前に、締切済みの問題へ回答を送らないためのチェックに使用）
  useEffect(() => {
    if (tenantSlug && lectureIdStr) {
      const tenantId = tenantSlug;
      const lectureId = lectureIdStr;
      setCheckAnswerableFn((questionId: number) => checkQuestionAnswerable(tenantId, lectureId, questionId));
    }
    return () => {
      setCheckAnswerableFn(null);
    };
  }, [tenantSlug, lectureIdStr, setCheckAnswerableFn]);

  // サーバー受理済み回答の確認関数を testStore に注入（STU-5対応）。
  // 「送信は成功したが応答を受け取れずリトライ→締切到達」のケースで、
  // サーバーに受理済みの回答をローカルの「未回答」で上書きしないための確認に使用する
  useEffect(() => {
    if (tenantSlug && lectureIdStr && currentUserId) {
      const tenantId = tenantSlug;
      const lectureId = lectureIdStr;
      const uid = currentUserId;
      setFetchServerAnswerFn(async (questionId: number) => {
        const history = await fetchAnswerHistory(tenantId, uid, lectureId);
        if (!history) return null;
        const record = history.answers.find((a) => a.question_id === questionId);
        return record ? record.choice_num : null;
      });
    }
    return () => {
      setFetchServerAnswerFn(null);
    };
  }, [tenantSlug, lectureIdStr, currentUserId, setFetchServerAnswerFn]);

  // questionsData 変更時に questionId → questionNum マッピングを更新
  // ただし、既にdisplayNumberが設定されている場合は上書きしない（リアルタイム問題を優先）
  useEffect(() => {
    if (questionsData?.questions) {
      questionsData.questions.forEach((q) => {
        // リアルタイムでdisplayNumberが設定されている場合はスキップ
        if (displayNumberMap[q.id] !== undefined) {
          console.log('[LecturePage] questionId', q.id, 'は既にdisplayNumber設定済みのためスキップ');
          return;
        }
        // S3のquestions.jsonではdisplay_numberが含まれていないため、nullを設定
        setQuestionNumMap(q.id, q.sort_key, null);
      });
    }
  }, [questionsData, setQuestionNumMap, displayNumberMap]);

  // リアルタイムモード時は useRealtimeChat のメッセージ、それ以外はローカルメッセージ
  const messages = useRealtime ? realtimeMessages : localMessages;

  // UI State
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [activeSideTab, setActiveSideTab] = useState<SideTab>('side-chat');
  const [activeSpTab, setActiveSpTab] = useState<SpTab>('overview');
  const [isSp, setIsSp] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [spTheaterRightTab, setSpTheaterRightTab] = useState<'test' | 'chat'>('test');
  
  // 講義終了10分後の強制リロードタイマー
  const lectureEndedReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SP版: answeringステータスで移動したかどうかを追跡
  const answeredQuestionIdsRef = useRef<Set<number>>(new Set());

  // 問題ごとの締切タイムスタンプ（リロード時のタイマー補正に使用）
  const scheduledCloseTimesRef = useRef<Record<number, number>>({});
  
  // リロード時の問題復元を初回のみ実行するためのフラグ
  const hasRestoredQuestionRef = useRef(false);

  // Refs
  const ivsPlayerRef = useRef<IVSPlayerHandle>(null);
  const pipBtnRef = useRef<HTMLButtonElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const spVideoWrapperRef = useRef<HTMLDivElement>(null);
  // EQプレイヤーコンテナdivのref
  const eqPlayerDivRef = useRef<HTMLDivElement | null>(null);
  const spStickyShellRef = useRef<HTMLDivElement>(null);
  const pageMode = isArchive ? 'archive' : 'live';
  const playbackUrl = lectureData?.live?.m3u8_url || '';
  // 講義ステータスに基づく状態（useMemoで定義してリアルタイム更新に対応）
  const isLectureEnded = useMemo(() => lectureData?.live?.status === 'ended', [lectureData?.live?.status]);
  const isLectureRunning = useMemo(() => lectureData?.live?.status === 'running', [lectureData?.live?.status]);
  const hasLiveStarted = useMemo(() => !!lectureData?.live?.started_at, [lectureData?.live?.started_at]);
  const isLiveStatusReady = useMemo(() => lectureData?.live?.status === 'ready', [lectureData?.live?.status]);
  const hasTestInProgress = useMemo(
    () => (questionsData?.questions ?? []).some(
      (q) => q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied',
    ),
    [questionsData],
  );

  // チャット投稿不可にするのは answering（回答中）のときのみ。tallied（解説中）は投稿可能
  const hasTestAnswering = useMemo(
    () => (questionsData?.questions ?? []).some((q) => q.status === 'answering'),
    [questionsData],
  );

  // チャット投稿の締切状態（講義終了 + CHAT_POST_GRACE_MS 経過）。
  // プレースホルダ文言と入力可否の両方がこの state を参照することで、
  // 締切時刻の到達時に文言と入力無効化が同時に切り替わる（SV-N3対応）。
  // Date.now() を useMemo 内で直接評価する方式は時刻経過で再計算されないため使わない
  const [isChatPostClosed, setIsChatPostClosed] = useState(false);

  // viewer.js の disableChat/enableChat に合わせたチャット制限メッセージ・プレースホルダー
  // 講義ステータス: ready(講義前), running(講義中), ended(講義終了)
  const chatInputPlaceholder = useMemo(() => {
    // 試用ユーザーの場合は常に投稿不可
    if (isTrialUser) return '試用ユーザーの方は投稿できません';
    
    // アーカイブモード
    if (isArchive) {
      return isSp ? 'アーカイブのため投稿できません' : 'アーカイブのため投稿できません（閲覧のみ）';
    }
    
    // 講義ステータス: ready（講義前）
    if (isLiveStatusReady) {
      return '講義開始後に利用できます';
    }
    
    // started_atが未設定の場合も講義前として扱う
    if (!hasLiveStarted) {
      return '講義開始後に利用できます';
    }
    
    // テスト回答中（answering）のときのみチャット不可。tallied（解説中）は投稿可能
    if (hasTestAnswering) return 'テスト終了までチャットは使用できません';
    
    // 講義ステータス: running（講義中）の場合は締切時刻に関係なくコメント入力可能メッセージを表示
    if (isLectureRunning) return 'チャットでコメント...';
    
    // 講義ステータス: ended（講義終了）
    // 締切判定は入力可否の useEffect（isChatPostClosed 更新元）に一元化しており、
    // 締切時刻到達時に state 変更 → 再レンダーで文言も同時に切り替わる
    if (isLectureEnded) {
      if (isChatPostClosed) {
        return 'チャットの投稿は終了しました';
      }
      return '講義終了後10分程投稿可能です';
    }
    
    // その他の場合はデフォルトメッセージ
    return 'チャットでコメント...';
  }, [isArchive, isSp, isLiveStatusReady, hasLiveStarted, hasTestAnswering, isLectureEnded, isLectureRunning, isTrialUser, isChatPostClosed]);

  // 資料の表示制御: distribution_type 0=講義前, 1=講義後。講義中は講義前のみ表示、終了後は全て表示
  // 試用ユーザーの場合は資料を非表示
  const visibleMaterials = useMemo(() => {
    const materials = lectureData?.materials ?? [];
    // 試用ユーザーの場合は資料を非表示
    if (isTrialUser) return [];
    if (isArchive || isLectureEnded) return materials;
    // 講義中: distribution_type が 0 または未設定のもののみ表示（1=講義後は非表示）
    return materials.filter((m) => m.distribution_type !== 1);
  }, [lectureData?.materials, isArchive, isLectureEnded, isTrialUser]);

  // PiP support check
  const isPiPSupported = useMemo(() => {
    if (typeof document === 'undefined') return false;
    return !!document.pictureInPictureEnabled;
  }, []);

  // PiP state
  const [isInPiP, setIsInPiP] = useState(false);

  // EQ Player: scriptタグ形式のパラメータ（コンテナID方式で初期化）
  const [eqParams, setEqParams] = useState<{
    b: string; c: string; m: string;
    s: Record<string, unknown>;
    ifJsUrl: string;
  } | null>(null);

  // Test timer effect - TestQuestionコンポーネント内部でタイマーを管理するため、ここでは不要

  // Fetch data on mount
  // isArchive は lectureData 由来のため、アーカイブ講義では初回フェッチ完了後に
  // false→true に変わり effect が再実行される。同一講義の再実行では再フェッチしない（STU-L1対応）
  // 取得処理本体は、初回ロード失敗時の「再試行」ボタンからも呼べるよう useCallback 化（STU-2対応）
  const initialFetchKeyRef = useRef<string | null>(null);
  const runInitialFetch = useCallback(() => {
    if (!tenantSlug || !internalId) return;

    // S3データ取得後、最新の講義ステータスをCDK APIから取得して上書きする
    fetchAllData(tenantSlug, internalId, false).then(() => {
      // アーカイブ判定はフェッチ完了後のデータで行う
      // （effect実行時点の isArchive は lectureData 未読込で常に false のため）
      const isArchiveAfterFetch =
        useLectureStore.getState().lectureData?.archive?.is_archive_published ?? false;
      // S3データ取得完了後、CDK APIから最新ステータスを取得
      if (!isArchiveAfterFetch && internalId) {
        fetchCurrentLectureStatus(tenantSlug, internalId).then((status) => {
          if (status) {
            console.log('[LecturePage] S3データ取得後、CDK APIステータスで上書き:', status);
            setLectureStatus(status);
          }
        });
      }
    });
  }, [tenantSlug, internalId, fetchAllData, setLectureStatus]);

  useEffect(() => {
    if (tenantSlug && internalId) {
      const fetchKey = `${tenantSlug}/${internalId}`;
      if (initialFetchKeyRef.current === fetchKey) return;
      initialFetchKeyRef.current = fetchKey;
      runInitialFetch();
    }
  }, [tenantSlug, internalId, runInitialFetch]);

  // リロード時: 現在の問題の進捗を復元する（WebSocket接続を待たずに即座に復元）
  // 復元処理の本体は syncCurrentQuestion（WebSocket再接続時の再同期と共通）
  // API取得完了後にWebSocket接続を許可する（時系列を保証）
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr || isArchive) return;
    if (hasRestoredQuestionRef.current) return; // 初回のみ実行

    // questionsDataが読み込まれた後に実行（S3データ取得完了後）
    if (questionsData) {
      syncCurrentQuestion();
    }
  }, [tenantSlug, lectureIdStr, isArchive, questionsData, syncCurrentQuestion]);

  // サーバー時刻オフセットの定期再測定（STU-M4対応）
  // オフセットは /questions/current の応答時にしか更新されないため、
  // 長時間の視聴セッションでは端末時計のドリフトで締切判定・タイマー表示がずれ得る。
  // ライブ視聴中は定期的に /questions/current を叩いて再測定する
  // （server_time の取り込みは fetchCurrentQuestion 内で行われる）。
  useEffect(() => {
    if (!useRealtimeTest || isArchive || !tenantSlug || !lectureIdStr) return;

    const RESYNC_INTERVAL_MS = 10 * 60 * 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      // 全端末の再測定が同時刻に揃わないよう ±10% のジッタを付ける
      const delay = RESYNC_INTERVAL_MS * (0.9 + Math.random() * 0.2);
      timer = setTimeout(async () => {
        await fetchCurrentQuestion(tenantSlug, lectureIdStr); // 失敗時はnullが返るだけなので次回に任せる
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [useRealtimeTest, isArchive, tenantSlug, lectureIdStr]);

  // 講義終了かつ questionsData が揃った時点で全問の集計データを CDK から取得する。
  // fetchAllData 完了後に講義ステータスが更新されるため、isLectureEndedとquestionsDataの
  // 両方が揃った時点で集計データを取得する。
  // 取得の実体・取得済み/実行中ガードは fetchAndApplyAggregations 側で共通化しており、
  // fetchAndApplyEndedQuestions が先に取得済みの場合はスキップされる（STU-H4対応）。
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr || isArchive) return;
    if (!isLectureEnded) return;
    const questions = questionsData?.questions ?? [];
    if (questions.length === 0) return;

    // 講義終了は全受講生に同時到達するため、ジッタで取得タイミングを分散する（STU-H4対応）
    const jitterMs = Math.floor(Math.random() * ENDED_FETCH_JITTER_MAX_MS);
    const timer = setTimeout(() => {
      console.log('[LecturePage] 講義終了 → 全問の集計データを一括取得:', questions.length, '問');
      void fetchAndApplyAggregations();
    }, jitterMs);
    return () => clearTimeout(timer);
  }, [tenantSlug, lectureIdStr, isArchive, isLectureEnded, questionsData, fetchAndApplyAggregations]);

  // 講義終了またはアーカイブの場合、/questions/ended から全問題データを取得し、
  // localStorageを全更新する（途中参加・講義終了後の問題一覧補完）。
  // 取得の実体・多重実行防止・成功済み判定は fetchAndApplyEndedQuestions 側で行うため、
  // 失敗時も lectureData の更新等を契機に再試行される。
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr) return;

    // 講義終了: lecture.live.is_live_published === true && lecture.live.status === 'ended'
    // アーカイブ: lecture.archive.is_archive_published === true
    const isLectureEndedCondition = lectureData?.live?.is_live_published && lectureData?.live?.status === 'ended';
    const isArchiveCondition = lectureData?.archive?.is_archive_published;

    if (!isLectureEndedCondition && !isArchiveCondition) {
      return;
    }

    // ライブの講義終了は全受講生に同時配信されるため jitter で分散する。
    // アーカイブ視聴はアクセスが自然に分散するため即時取得する。
    void fetchAndApplyEndedQuestions({ jitter: !isArchiveCondition });
  }, [tenantSlug, lectureIdStr, lectureData, fetchAndApplyEndedQuestions]);

  // ページアクセス時に回答履歴を取得する（講義ステータスに関わらず実行）
  // answerHistoryFetchedRef は「取得・反映に成功した後」にのみ true にする。
  // 失敗時は false のままにし、次の契機（questionsData 更新等）で再試行できるようにする
  // （/questions/ended・/aggregations と同じ方針。SV-N2対応）。
  // 多重実行は answerHistoryFetchInFlightRef で防止する。
  const answerHistoryFetchedRef = useRef(false);
  const answerHistoryFetchInFlightRef = useRef(false);
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr || !userId) return;
    if (!questionsData?.questions || questionsData.questions.length === 0) return;

    // 取得成功済み・実行中は何もしない
    if (answerHistoryFetchedRef.current || answerHistoryFetchInFlightRef.current) return;

    console.log('[LecturePage] ページアクセス時: 回答履歴を取得');

    const fetchAnswerHistoryData = async () => {
      answerHistoryFetchInFlightRef.current = true;
      try {
        const answerHistory = await fetchAnswerHistory(tenantSlug, userId, lectureIdStr);

        if (answerHistory) {
          console.log('[LecturePage] /answers/history から回答履歴を取得:', answerHistory.answers.length, '件');

          // question_id から questionId へのマップと講義内の全問題IDリストを作成
          const questionIdMap: Record<number, number> = {};
          const lectureQuestionIds: number[] = [];
          questionsData.questions.forEach((q) => {
            questionIdMap[q.id] = q.id;
            lectureQuestionIds.push(q.id);
          });

          // サーバーの回答履歴を正として、ストア・localStorage と突き合わせる。
          // 画面を閉じている間の「中止（回答リセット）」で TestCancelled を受信できず
          // ローカルに残った削除済みの旧回答をここで確実にクリアする（サーバーが正）
          reconcileAnswersWithServer(answerHistory.answers, questionIdMap, lectureQuestionIds);

          console.log('[LecturePage] 回答履歴をストアに反映完了');
          // 取得・反映に成功した場合のみ完了フラグを立てる
          answerHistoryFetchedRef.current = true;
        } else {
          // null は APIエラー等で回答の有無を判定できないケース。
          // 誤削除を避けるためローカルデータには手を付けない。
          // フラグは立てず、次の契機で再試行する
          console.warn('[LecturePage] 回答履歴を取得できないためローカルデータを維持（次の契機で再試行します）');
        }
      } catch (error) {
        console.warn('[LecturePage] 回答履歴取得エラー（次の契機で再試行します）:', error);
      } finally {
        answerHistoryFetchInFlightRef.current = false;
      }
    };

    fetchAnswerHistoryData();
  }, [tenantSlug, lectureIdStr, userId, questionsData, questionNumMap, reconcileAnswersWithServer]);

  // 講義終了時・アーカイブ時にランキング情報を取得する
  // rankingFetchedRef は「取得に成功した後」にのみ true にする。
  // 失敗時は false のままにし、次の契機（lectureData 更新等）で再試行できるようにする
  // （/questions/ended・/aggregations と同じ方針。SV-N2対応）。
  // 多重実行は rankingFetchInFlightRef（フェッチ中）と
  // rankingFetchTimerRef（ジッタ待機中）で防止する。
  const rankingFetchedRef = useRef(false);
  const rankingFetchInFlightRef = useRef(false);
  // ジッタ待機タイマー。effect のクリーンアップでは破棄しない
  // （deps の頻繁な更新でタイマーが破棄されると取得されなくなるため。
  //   破棄は講義やり直し時・講義ID変更時・アンマウント時のみ行う）
  const rankingFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!tenantSlug || !lectureIdStr || !userId) return;
    if (!questionsData?.questions || questionsData.questions.length === 0) return;
    
    // 講義終了またはアーカイブの場合のみ実行
    const isLectureEndedCondition = lectureData?.live?.is_live_published && lectureData?.live?.status === 'ended';
    const isArchiveCondition = lectureData?.archive?.is_archive_published;
    
    if (!isLectureEndedCondition && !isArchiveCondition) {
      // 講義がやり直された（ended → ready/running）場合に備えてフラグをリセットし、
      // 次回の講義終了で再取得できるようにする（endedQuestionsFetchedRef と同方針）
      rankingFetchedRef.current = false;
      if (rankingFetchTimerRef.current) {
        clearTimeout(rankingFetchTimerRef.current);
        rankingFetchTimerRef.current = null;
      }
      return;
    }
    
    // 取得成功済み・フェッチ実行中・ジッタ待機中は何もしない
    if (rankingFetchedRef.current) return;
    if (rankingFetchInFlightRef.current) return;
    if (rankingFetchTimerRef.current !== null) return;

    console.log('[LecturePage] 講義終了/アーカイブ時: ランキング情報を取得');

    const fetchRankingData = async () => {
      rankingFetchInFlightRef.current = true;
      try {
        const rankingData = await fetchUserRanking(tenantSlug, userId, lectureIdStr);
        
        if (rankingData && rankingData.ranking) {
          console.log('[LecturePage] /user-ranking からランキング情報を取得:', rankingData.ranking);
          
          // ランキング情報をストアに設定
          setUserRanking({
            rank: rankingData.ranking.rank,
            total_users: rankingData.ranking.total_users,
            percentile: rankingData.ranking.percentile,
            tier: rankingData.ranking.tier,
          });
          
          console.log('[LecturePage] ランキング情報をストアに反映完了');
        } else {
          // ランキングなし（未回答またはデータなし）は正常応答なので成功扱いにする
          console.log('[LecturePage] ランキング情報が見つかりません（未回答またはデータなし）');
        }
        // 取得に成功した場合のみ完了フラグを立てる
        rankingFetchedRef.current = true;
      } catch (error) {
        console.warn('[LecturePage] ランキング情報取得エラー（次の契機で再試行します）:', error);
      } finally {
        rankingFetchInFlightRef.current = false;
      }
    };

    // ライブの講義終了は全受講生に同時到達するため、/user-ranking への一斉アクセスを
    // ジッタで分散する（STU-9対応。/questions/ended・/aggregations と同じ方式）。
    // アーカイブ視聴はアクセスが自然に分散するため即時取得する
    const jitterMs = isArchiveCondition ? 0 : Math.floor(Math.random() * ENDED_FETCH_JITTER_MAX_MS);
    rankingFetchTimerRef.current = setTimeout(() => {
      rankingFetchTimerRef.current = null;
      void fetchRankingData();
    }, jitterMs);
  }, [tenantSlug, lectureIdStr, userId, questionsData, lectureData, setUserRanking]);

  // ランキング取得のジッタ待機タイマーをアンマウント時に破棄する
  useEffect(() => {
    return () => {
      if (rankingFetchTimerRef.current) {
        clearTimeout(rankingFetchTimerRef.current);
        rankingFetchTimerRef.current = null;
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // 講義ID変更時のストア・ref リセット（S3対応・受講生B-1）
  // SPA 内で別講義へ遷移（履歴 back/forward 含む）した場合、React Router は
  // 同一 Route の param 変更でコンポーネントを再マウントしないため、
  // Zustand ストア（前講義の回答・問題・チャット）と「1回のみ実行」ref 群が残留する。
  // useLayoutEffect は同一コミット内のすべての useEffect（データ取得等）より先に
  // 実行されるため、リセット→新講義データ取得の順序が保証される。
  // uiStore はユーザーのUI設定（シアターモード等）のためリセットしない。
  // ------------------------------------------------------------------
  const prevInternalIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!internalId) return;
    const prevInternalId = prevInternalIdRef.current;
    prevInternalIdRef.current = internalId;
    if (!prevInternalId || prevInternalId === internalId) return;

    console.log('[LecturePage] 講義IDが変わったためストアをリセット:', prevInternalId, '→', internalId);
    useTestStore.getState().reset();
    useChatStore.getState().reset();
    useLectureStore.getState().reset();

    // 「1回のみ実行」ref 群・進行中状態もリセットして新講義でのデータ取得を許可する
    aggregationFetchedRef.current = false;
    endedQuestionsFetchedRef.current = false;
    endedQuestionsFetchInFlightRef.current = false;
    answerHistoryFetchedRef.current = false;
    answerHistoryFetchInFlightRef.current = false;
    rankingFetchedRef.current = false;
    rankingFetchInFlightRef.current = false;
    if (rankingFetchTimerRef.current) {
      clearTimeout(rankingFetchTimerRef.current);
      rankingFetchTimerRef.current = null;
    }
    hasRestoredQuestionRef.current = false;
    syncInFlightRef.current = false;
    lastQuestionEventAtRef.current = 0;
    scheduledCloseTimesRef.current = {};
    answeredQuestionIdsRef.current = new Set();
    setIsQuestionApiReady(false);
    setIsChatPostClosed(false);
    if (lectureEndedReloadTimerRef.current) {
      clearTimeout(lectureEndedReloadTimerRef.current);
      lectureEndedReloadTimerRef.current = null;
    }
  }, [internalId]);

  // SP viewport detection (mockup_v2_SP-asano02 に合わせ 1023px 以下を SP)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handle = () => setIsSp(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // SP: 横向き（landscape）検出 → isLandscape state を更新（body 属性は body attrs useEffect で制御）
  useEffect(() => {
    const updateOrientation = () => {
      const landscape = window.matchMedia('(orientation: landscape)').matches;
      setIsLandscape(landscape);
    };
    updateOrientation();
    window.addEventListener('resize', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
    };
  }, []);

  // Set body attributes for CSS
  useEffect(() => {
    document.body.classList.add('viewer');
    document.body.setAttribute('data-page', pageMode);
    document.body.setAttribute('data-player', isArchive ? 'eq' : 'ivs');
    
    if (isTheaterMode) {
      document.body.setAttribute('data-mode', 'theater');
      document.body.setAttribute('data-main-tabs', 'none');
    } else {
      document.body.removeAttribute('data-mode');
      document.body.removeAttribute('data-main-tabs');
    }

    if (!isSidebarVisible) {
      document.body.setAttribute('data-sidebar', 'hidden');
    } else {
      document.body.removeAttribute('data-sidebar');
    }

    document.body.setAttribute('data-sp-tab', activeSpTab);

    if (isSp) {
      document.body.setAttribute('data-sp-fixed-shell', '1');
    } else {
      document.body.removeAttribute('data-sp-fixed-shell');
    }

    // SP シアター横画面: landscape + theater 時のみ layout 属性を設定
    if (isSp && isTheaterMode && isLandscape) {
      document.body.setAttribute('data-sp-theater-layout', 'landscape');
    } else {
      document.body.removeAttribute('data-sp-theater-layout');
    }

    // SP シアター横画面: 右パネルのアクティブタブを body 属性で制御
    document.body.setAttribute('data-sp-theater-right-tab', spTheaterRightTab);

    if (isSp && !isArchive) {
      document.body.setAttribute('data-sp-has-message', hasMessage ? '1' : '0');
    } else {
      document.body.removeAttribute('data-sp-has-message');
    }

    return () => {
      document.body.classList.remove('viewer');
      document.body.removeAttribute('data-page');
      document.body.removeAttribute('data-player');
      document.body.removeAttribute('data-mode');
      document.body.removeAttribute('data-main-tabs');
      document.body.removeAttribute('data-sidebar');
      document.body.removeAttribute('data-sp-tab');
      document.body.removeAttribute('data-sp-fixed-shell');
      document.body.removeAttribute('data-sp-theater-layout');
      document.body.removeAttribute('data-sp-theater-right-tab');
      document.body.removeAttribute('data-sp-has-message');
    };
  }, [pageMode, isArchive, isTheaterMode, isSidebarVisible, activeSpTab, isSp, isLandscape, spTheaterRightTab, hasMessage]);

  // SP: ヘッダー高さを実測してプレイヤー位置を密着させる（プレイヤー上の余白除去）
  // CSS変数はbody要素に設定（body.viewerで定義済みの初期値を上書きするため）
  useEffect(() => {
    if (!isSp) return;
    const header = document.querySelector<HTMLElement>('.header');
    const setHeaderHeight = () => {
      if (header) {
        document.body.style.setProperty('--sp-fixed-header-height', `${header.offsetHeight}px`);
      }
    };
    setHeaderHeight();
    const ro = new ResizeObserver(setHeaderHeight);
    if (header) ro.observe(header);
    return () => ro.disconnect();
  }, [isSp]);

  // SP: 固定シェル（動画+メッセージ+アンケート帯）の高さを実測し、sp-content の top を正確に設定
  const hasSurvey = isArchive && !isTrialUser && !!lectureData?.survey_url;
  const updateSpShellHeight = useCallback(() => {
    const shell = spStickyShellRef.current;
    if (!shell) return;
    const shellH = Math.ceil(shell.getBoundingClientRect().height);
    if (shellH >= 0) {
      document.body.style.setProperty('--sp-shell-height', `${shellH}px`);
    }
  }, []);
  useLayoutEffect(() => {
    if (!isSp) return;
    updateSpShellHeight();
    const shell = spStickyShellRef.current;
    const inner = shell?.querySelector<HTMLElement>('.sp-sticky-shell-inner');
    const ro = new ResizeObserver(updateSpShellHeight);
    if (shell) ro.observe(shell);
    if (inner) ro.observe(inner);
    window.addEventListener('resize', updateSpShellHeight);
    window.addEventListener('orientationchange', updateSpShellHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSpShellHeight);
      window.removeEventListener('orientationchange', updateSpShellHeight);
    };
  }, [isSp, isArchive, hasMessage, hasSurvey, lectureData?.message?.title, lectureData?.message?.contents, updateSpShellHeight]);

  // チャット入力を有効/無効化
  // 講義ステータス: ready(講義前), running(講義中), ended(講義終了)
  // テストステータスは講義ステータスとは別に管理される
  useEffect(() => {
    const clearReloadTimer = () => {
      if (lectureEndedReloadTimerRef.current) {
        clearTimeout(lectureEndedReloadTimerRef.current);
        lectureEndedReloadTimerRef.current = null;
      }
    };

    // 試用ユーザーの場合は常に無効
    if (isTrialUser) {
      clearReloadTimer();
      setInputEnabled(false);
      return;
    }

    // アーカイブモードの場合は常に無効
    if (isArchive) {
      clearReloadTimer();
      setInputEnabled(false);
      return;
    }

    // 講義ステータス: ready（講義前）の場合は無効
    // 講義がやり直された（ended → ready）場合に備えて締切状態もリセットする
    if (isLiveStatusReady || !hasLiveStarted) {
      clearReloadTimer();
      setInputEnabled(false);
      setIsChatPostClosed(false);
      return;
    }

    // 講義ステータス: running（講義中）の場合は締切時刻に関係なく有効化
    // ただし、テスト回答中（answering）のときのみ無効。tallied（解説中）は投稿可能
    // 講義がやり直された（ended → running）場合に備えて締切状態もリセットする
    if (isLectureRunning) {
      clearReloadTimer();
      setInputEnabled(!hasTestAnswering);
      setIsChatPostClosed(false);
      return;
    }

    // 講義ステータス: ended（講義終了）の場合は actual_ended_at + 10分以内であれば有効。
    // 締切判定はサーバー時刻補正（serverNow）で行う（端末時計ずれ対策。SV-N3対応）
    if (isLectureEnded) {
      const actualEndedAt = lectureData?.archive?.actual_ended_at;
      if (actualEndedAt) {
        const chatClosingTime = new Date(actualEndedAt).getTime() + CHAT_POST_GRACE_MS;
        const now = serverNow();

        if (now < chatClosingTime) {
          // 10分以内 → チャット有効。閉じる時刻に入力無効化をスケジュール（未設定の場合のみ）
          setInputEnabled(true);
          setIsChatPostClosed(false);
          if (!lectureEndedReloadTimerRef.current) {
            lectureEndedReloadTimerRef.current = setTimeout(() => {
              lectureEndedReloadTimerRef.current = null;
              setInputEnabled(false);
              // プレースホルダ文言「チャットの投稿は終了しました」も同時に切り替える
              setIsChatPostClosed(true);
            }, chatClosingTime - now);
          }
        } else {
          // 10分超過 → チャット無効
          clearReloadTimer();
          setInputEnabled(false);
          setIsChatPostClosed(true);
        }
      }
      // actual_ended_at が未設定 = S3再フェッチ待ち。チャット状態は変更しない
      return;
    }

    // その他の場合は無効
    clearReloadTimer();
    setInputEnabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setInputEnabled は Zustand の安定した関数
  }, [isArchive, isLiveStatusReady, hasLiveStarted, isLectureRunning, isLectureEnded, hasTestAnswering, isTrialUser, lectureData?.archive?.actual_ended_at]);

  // コンポーネントアンマウント時にリロードタイマーをクリーンアップ
  useEffect(() => {
    return () => {
      if (lectureEndedReloadTimerRef.current) {
        clearTimeout(lectureEndedReloadTimerRef.current);
      }
    };
  }, []);

  // SP版: テストのステータスがansweringになったら1度だけテストタブに強制移動
  useEffect(() => {
    if (!isSp) return;
    if (!questionsData?.questions) return;
    
    // answeringステータスの問題を取得
    const answeringQuestions = questionsData.questions.filter(
      (q) => q.status === 'answering'
    );
    
    // 新しいansweringステータスの問題（まだ移動していない問題）を検出
    const newAnsweringQuestions = answeringQuestions.filter(
      (q) => !answeredQuestionIdsRef.current.has(q.id)
    );
    
    // 新しいansweringステータスの問題があり、現在テストタブでない場合のみ移動
    if (newAnsweringQuestions.length > 0 && activeSpTab !== 'test') {
      setActiveSpTab('test');
      // 移動した問題のIDを記録
      newAnsweringQuestions.forEach((q) => {
        answeredQuestionIdsRef.current.add(q.id);
      });
    }
    
    // answeringステータスが解除された問題のIDを削除（再度answeringになったときに移動できるように）
    questionsData.questions.forEach((q) => {
      if (q.status !== 'answering' && answeredQuestionIdsRef.current.has(q.id)) {
        answeredQuestionIdsRef.current.delete(q.id);
      }
    });
  }, [isSp, questionsData, activeSpTab]);

  // 表示用メッセージ: hidden・NGユーザー・NGワードを除外（投稿者本人は除く）
  // 自動スクロール・表示上限は ChatMessages コンポーネント側で制御する
  const visibleChatMessages = useMemo(
    () => {
      return messages.filter((msg) => {
        // 投稿者本人のメッセージは常に表示（非表示、NGユーザー、NGワードでも）
        const isOwnMessage = currentUserId && msg.userId === currentUserId;
        if (isOwnMessage) return true;
        
        // NGユーザーまたはNGワードを含むメッセージは投稿者本人以外には非表示
        if (msg.hasNgWords || msg.isNgUser) return false;
        
        // 非表示メッセージは投稿者本人以外には非表示
        if (msg.isHidden || msg.status === 'hidden') return false;
        
        return true;
      });
    },
    [messages, currentUserId],
  );

  // EQ Player Step1: プレイヤー初期化パラメータ（b/c/m/s）を解決する
  // 1: 講義JSONの archive.eq_player（base_path/contract_id/video_id）から直接組み立てる
  // 2: archive.embed_script（貼り付けタグ）をパースして抽出する
  useEffect(() => {
    if (!isArchive) {
      setEqParams(null);
      return;
    }

    // --- 1: eq_player パラメータから直接生成（埋め込みコード不要） ---
    const eqPlayer = lectureData?.archive.eq_player;
    if (eqPlayer?.base_path && eqPlayer?.contract_id && eqPlayer?.video_id) {
      const basePath = eqPlayer.base_path.trim();
      setEqParams({
        b: `${basePath}.eq.webcdn.stream.ne.jp/www50/${basePath}/jmc_pub/jmc_swf/player/`,
        c: eqPlayer.contract_id,
        m: eqPlayer.video_id,
        // EQ標準の貼り付けタグと同等のプレイヤー設定（rp:fit は画面フィット表示用）
        s: {
          bskb: '10',
          cpt: 'off',
          dq: '0',
          el: 'off',
          fskb: '10',
          hp: 360,
          il: 'off',
          ip: 'on',
          is: 'on',
          mdq: '0',
          pr: '0.5,0.7,1.2,1.5,2.0',
          rp: 'fit',
          rs: 'on',
          sn: '',
          tg: 'off',
          ti: 'off',
          wp: 640,
        },
        ifJsUrl: `https://ssl-cache.stream.ne.jp/www50/${basePath}/jmc_pub/jmc_swf/player/t3/if.js`,
      });
      return;
    }

    // --- 2: embed_script のパース ---
    if (!lectureData?.archive.embed_script) {
      setEqParams(null);
      return;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = lectureData.archive.embed_script;
    const scripts = Array.from(tempDiv.querySelectorAll('script'));

    const ifJsEl = scripts.find((s) => s.getAttribute('src')?.includes('/t3/if.js'));
    const inlineEl = scripts.find((s) => !s.getAttribute('src') && s.textContent?.includes('PlayerFactoryIF.create'));

    if (!inlineEl?.textContent) {
      console.warn('[EQ Player] embed_script にインラインスクリプトが見つかりません。scriptタグ形式で設定してください。');
      setEqParams(null);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origJstream = (window as any).jstream_t3;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let captured: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).jstream_t3 = { PlayerFactoryIF: { create: (p: any) => { captured = p; } } };

    const execEl = document.createElement('script');
    execEl.textContent = inlineEl.textContent;
    document.body.appendChild(execEl);
    execEl.remove();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (origJstream !== undefined) { (window as any).jstream_t3 = origJstream; }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    else { delete (window as any).jstream_t3; }

    if (!captured?.b || !captured?.c || !captured?.m) {
      console.warn('[EQ Player] パラメータ取得失敗:', captured);
      setEqParams(null);
      return;
    }

    const ifJsUrl = ifJsEl?.getAttribute('src') ||
      `https://ssl-cache.stream.ne.jp/www50/${captured.b.split('.')[0]}/jmc_pub/jmc_swf/player/t3/if.js`;

    setEqParams({
      b: captured.b,
      c: captured.c,
      m: captured.m,
      s: { ...(captured.s || {}), rp: 'fit' },
      ifJsUrl,
    });
  }, [isArchive, lectureData?.archive.eq_player, lectureData?.archive.embed_script]);

  // EQ Player Step2: #eqplayer div を body 直下に配置し EQ Player SDK を初期化
  // 重要: iframe は appendChild で別の親に移動するとブラウザ仕様上リロードされやすい。
  // そのため動画 div は React 管理外で生成し、一度 body 直下に置いた後は移動させない。
  // Step3 で getBoundingClientRect により video-wrapper の位置・サイズに重ねて SP/PC リサイズに追従。
  // 座標系: PC通常=absolute+scroll、シアター/SP固定=fixed+viewport（内部スクロール対応）
  useEffect(() => {
    if (!eqParams) return;

    const { b, c, m, s, ifJsUrl } = eqParams;

    // div を body 直下に絶対配置（一度だけ append、以後絶対に移動しない）
    // position:absolute + scrollY/X 加算でページ内絶対座標で追従する設計。
    // z-index は viewer.css の body[data-page="archive"] ルールでモード別に制御
    const playerDiv = document.createElement('div');
    playerDiv.id = 'eqplayer';
    playerDiv.style.cssText = 'top:0;left:0;width:0;height:0;background:#000;overflow:hidden;';
    document.body.appendChild(playerDiv);
    eqPlayerDivRef.current = playerDiv;

    // 初期位置・サイズを設定。React のレンダリングタイミングで ref が未アタッチの場合もあるため
    // RAF でリトライしながら確実に位置を反映する（最大 2 秒）
    let initRafId: number | null = null;
    let initAttempts = 0;
    const maxInitAttempts = 120;
    const tryInitPosition = () => {
      initRafId = null;
      const isSpNow = window.matchMedia('(max-width: 1023px)').matches;
      const target = isSpNow ? spVideoWrapperRef.current : videoWrapperRef.current;
      if (target && applyEqPlayerRect(playerDiv, target)) {
        return;
      }
      if (initAttempts < maxInitAttempts) {
        initAttempts++;
        initRafId = requestAnimationFrame(tryInitPosition);
      }
    };
    tryInitPosition();

    const getVideoWrapper = (): HTMLElement | null => {
      const isSpNow = window.matchMedia('(max-width: 1023px)').matches;
      return isSpNow ? spVideoWrapperRef.current : videoWrapperRef.current;
    };

    let cleanupScrollOverlay: (() => void) | null = null;

    // 既存の if.js を削除してフレッシュロード（重複読み込み防止）
    const existing = document.querySelector(`script[src="${ifJsUrl}"]`);
    if (existing) existing.remove();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window as any).jstream_t3;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.src = ifJsUrl;

    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jstream = (window as any).jstream_t3;
      if (!jstream?.PlayerFactoryIF) {
        console.warn('[EQ Player] if.js loaded but jstream_t3 is unavailable');
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).player = jstream.PlayerFactoryIF.create({ b, c, m, s }, 'eqplayer');
        document.querySelector('.archive-loading-placeholder')?.remove();
        cleanupScrollOverlay?.();
        cleanupScrollOverlay = setupEqPlayerScrollOverlay(playerDiv, getVideoWrapper);
        requestAnimationFrame(() => {
          requestAnimationFrame(updateSpShellHeight);
        });
      } catch (e) {
        console.error('[EQ Player] create() エラー:', e);
      }
    };

    script.onerror = () => console.error('[EQ Player] if.js 読み込み失敗:', ifJsUrl);
    document.head.appendChild(script);

    return () => {
      if (initRafId !== null) cancelAnimationFrame(initRafId);
      cleanupScrollOverlay?.();
      cleanupScrollOverlay = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p = (window as any).player;
      if (p?.accessor?.pause) { try { p.accessor.pause(); } catch { /**/ } }
      const cleanup = document.querySelector(`script[src="${ifJsUrl}"]`);
      if (cleanup) cleanup.remove();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).jstream_t3;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any).player;
      playerDiv.remove();
      eqPlayerDivRef.current = null;
      const theaterBtn = document.getElementById('sp-theater-toggle-btn-sp');
      if (theaterBtn) {
        theaterBtn.style.position = '';
        theaterBtn.style.zIndex = '';
        theaterBtn.style.top = '';
        theaterBtn.style.left = '';
      }
    };
  }, [eqParams, updateSpShellHeight]);

  // EQ Player Step3: アクティブな video-wrapper の位置・サイズに #eqplayer を追従させる
  // ResizeObserver で wrapper のサイズ変化を、scroll/resize で位置変化を検知して追従
  useEffect(() => {
    if (!isArchive || !eqParams) return;

    let rafId: number | null = null;
    let lastTop = -1;
    let lastLeft = -1;
    let lastWidth = -1;
    let lastHeight = -1;
    let lastBorderRadius = '';
    let lastUseFixed = false;
    let lastHeaderBottom = -1;

    const applyRect = () => {
      const playerDiv = eqPlayerDivRef.current;
      if (!playerDiv) return false;
      const target = isSp ? spVideoWrapperRef.current : videoWrapperRef.current;
      if (!target) return false;
      const rect = target.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;

      const useFixed = isFixedEqPlayerPosition();
      const headerBottom = getSpVisibleHeaderBottom() ?? -1;
      const top = useFixed ? rect.top : rect.top + window.scrollY;
      const left = useFixed ? rect.left : rect.left + window.scrollX;
      const borderRadius = window.getComputedStyle(target).borderRadius;
      if (
        top !== lastTop ||
        left !== lastLeft ||
        rect.width !== lastWidth ||
        rect.height !== lastHeight ||
        borderRadius !== lastBorderRadius ||
        useFixed !== lastUseFixed ||
        headerBottom !== lastHeaderBottom
      ) {
        applyEqPlayerRect(playerDiv, target);
        lastTop = top;
        lastLeft = left;
        lastWidth = rect.width;
        lastHeight = rect.height;
        lastBorderRadius = borderRadius;
        lastUseFixed = useFixed;
        lastHeaderBottom = headerBottom;
      } else {
        syncTheaterHeaderClip(playerDiv, rect);
        if (isSp) syncSpTheaterToggleButton(target);
      }
      return true;
    };

    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        applyRect();
      });
    };

    // 初期描画。ref が未アタッチの場合は RAF で 2 秒間リトライ
    let retryRafId: number | null = null;
    let retryAttempts = 0;
    const initialApply = () => {
      retryRafId = null;
      if (applyRect()) return;
      if (retryAttempts < 120) {
        retryAttempts++;
        retryRafId = requestAnimationFrame(initialApply);
      }
    };
    initialApply();

    // ResizeObserver: アクティブな wrapper のサイズ変化を監視
    const target = isSp ? spVideoWrapperRef.current : videoWrapperRef.current;
    let observer: ResizeObserver | null = null;
    if (target && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(target);
    }
    // body のサイズ変化（レイアウト全体のシフト）も監視
    let bodyObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      bodyObserver = new ResizeObserver(scheduleUpdate);
      bodyObserver.observe(document.body);
    }
    const headerEl = document.querySelector('.header');
    let headerObserver: ResizeObserver | null = null;
    if (headerEl && typeof ResizeObserver !== 'undefined') {
      headerObserver = new ResizeObserver(scheduleUpdate);
      headerObserver.observe(headerEl);
    }

    document.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (retryRafId !== null) cancelAnimationFrame(retryRafId);
      if (observer) observer.disconnect();
      if (bodyObserver) bodyObserver.disconnect();
      if (headerObserver) headerObserver.disconnect();
      document.removeEventListener('scroll', scheduleUpdate, { capture: true });
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [isSp, isArchive, eqParams, isTheaterMode]);

  // Handlers
  const toggleTheaterMode = useCallback(() => {
    setIsTheaterMode((prev) => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible((prev) => !prev);
  }, []);


  const handleSideTabClick = useCallback((tab: SideTab) => {
    setActiveSideTab(tab);
  }, []);

  const handleSpTabClick = useCallback((tab: SpTab) => {
    setActiveSpTab(tab);
    // ユーザーが手動でタブを変更した場合、現在answeringステータスの問題の移動フラグを設定
    // これにより、再度自動移動しないようにする
    if (tab !== 'test' && questionsData?.questions) {
      questionsData.questions.forEach((q) => {
        if (q.status === 'answering') {
          answeredQuestionIdsRef.current.add(q.id);
        }
      });
    }
  }, [questionsData]);

  // テスト問題をフィルタリングする共通関数
  const filterQuestions = useCallback((questions: Question[] | undefined): Question[] => {
    if (!questions) {
      return [];
    }
    
    return questions.filter((question) => {
      if (question.status === 'not_started') {
        return false;
      }
      if (question.status === 'completed') {
        return false;
      }
      return true;
    });
  }, []);

  // 送信の成否を boolean で返す（失敗時は ChatInput 側で入力内容を保持して再送信を可能にする。STU-6対応）
  const handleChatSubmit = useCallback(async (message: string): Promise<boolean> => {
    const trimmed = message.trim();
    if (!trimmed || !isInputEnabled) return false;

    if (useRealtime) {
      // リアルタイムモード: 空・文字数・投稿期限のみクライアントで検証して WebSocket で送信
      // NGワード・NGユーザーの判定はサーバー側（onPublish Lambda）が行い、
      // hasNgWords / isNgUser フラグ付きで保存・配信されるため、クライアントでは事前ブロックしない
      const validation = validateAndCheck(trimmed, Number(currentUserId) || 0, [], []);
      if (!validation.isValid) return false;

      try {
        await sendRealtimeMessage({
          userId: currentUserId,
          userName: currentUserName,
          message: trimmed,
          userType: 'student',
        });
        return true;
      } catch (err) {
        // 送信失敗を黙殺せず受講生に通知する（STU-6対応）
        console.error('[Chat] メッセージ送信エラー:', err);
        setErrorMessage('チャットメッセージを送信できませんでした。時間をおいて再度お試しください。');
        return false;
      }
    } else {
      // モックモード: ローカルにメッセージ追加
      submitMessage(trimmed, Number(currentUserId) || 0, currentUserName, ngWords, ngUsers);
      return true;
    }
  }, [isInputEnabled, useRealtime, validateAndCheck, sendRealtimeMessage, submitMessage, ngWords, ngUsers, currentUserId, currentUserName, setErrorMessage]);

  // Format timestamp for chapters (MM:SS format)
  const formatTimestamp = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  // Handle chapter click (seek video)
  const handleChapterClick = useCallback(async (seconds: number) => {
    if (isArchive) {
      // EQプレイヤーの accessor 経由でシーク。準備完了まで最大4秒リトライ
      const trySeek = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const accessor = (window as any).player?.accessor;
        if (!accessor || typeof accessor.setCurrentTime !== 'function') return false;
        try {
          accessor.setCurrentTime(seconds);
          if (accessor.state === 'paused' && typeof accessor.play === 'function') accessor.play();
          return true;
        } catch { return false; }
      };
      const start = Date.now();
      while (!trySeek() && Date.now() - start < 4000) {
        await new Promise((r) => setTimeout(r, 150));
      }
    } else {
      ivsPlayerRef.current?.seekTo(seconds);
    }
  }, [isArchive]);

  // PiP handlers
  const handleTogglePiP = useCallback(async () => {
    const video = ivsPlayerRef.current?.getVideoElement();
    if (!video || !isPiPSupported) return;

    try {
      if (document.pictureInPictureElement === video) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch (e) {
      console.warn('[PiP] 操作に失敗しました:', e);
    }
  }, [isPiPSupported]);

  // テスト問題をレンダリングする共通関数
  const renderTestQuestions = useCallback((showPiPControls: boolean = false) => {
    const filteredQuestions = filterQuestions(questionsData?.questions);
    
    return filteredQuestions.map((question) => {
      const isAnswering = question.status === 'answering';
      const isTestStarted = isAnswering || question.status === 'tallying' || question.status === 'tallied';
      const isTestCompleted = false; // completedは表示しないため常にfalse
      const isExplaining = question.status === 'tallied'; // tallied = 集計完了 = 解説中
      const selectedChoiceId = selectedChoices[question.id];
      const isAnswerSent = submittedQuestions.has(question.id);
      const isSendingAnswer = answerSendStates[question.id] === 'sending';
      const isSendFailed = answerSendStates[question.id] === 'failed';
      const isSubmitted = isExplaining;
      
      // questionNumMap と displayNumberMap から取得
      const questionNum = questionNumMap[question.id];
      const displayNumber = displayNumberMap[question.id];

      // 回答分布データを TestResult[] 形式に変換
      // （未回答エントリは付加しない。正答率の母数は回答数のみ＝管理画面・集計APIと同一定義。G3対応）
      const aggregation = questionAggregations[question.id];
      const results: TestResultType[] = aggregation
        ? question.choices.map((c, idx) => {
            const key = `choice_${idx + 1}`;
            const count = aggregation.choiceCounts[key] || 0;
            return {
              choiceId: c.id,
              count,
              percentage: aggregation.totalAnswers > 0
                ? (count / aggregation.totalAnswers) * 100 : 0,
            };
          })
        : [];

      return (
        <div key={question.id} className="test-section">
          {/* テスト中：動画をPiPで表示（PC版のみ） */}
          {showPiPControls && isTestStarted && !isTestCompleted && (
            <div className="test-pip-controls">
              <div className="test-pip-hint">テスト中も動画を見ながら解答できます</div>
              <div className="test-pip-actions">
                <button
                  id="pip-toggle-btn-test"
                  className="test-pip-btn"
                  type="button"
                  aria-label="動画をPiPで表示"
                  aria-pressed={isInPiP}
                  title="動画をPiPで表示"
                  onClick={handleTogglePiP}
                  disabled={!isPiPSupported}
                >
                  {isInPiP ? 'PiP終了' : '動画をPiPで表示'}
                </button>
              </div>
            </div>
          )}

          {/* TestQuestionコンポーネントを使用 */}
          <TestQuestion
            question={question}
            questionNum={questionNum}
            displayNumber={displayNumber}
            selectedChoiceId={selectedChoiceId}
            isSubmitted={isSubmitted}
            isAnswerSent={isAnswerSent}
            isSending={isSendingAnswer}
            isSendFailed={isSendFailed}
            onSelectChoice={(choiceId) => selectChoice(question.id, choiceId)}
            onSubmit={() => {
              submitAnswer(question.id);
              // タイマーは停止しない（制限時間まで継続、再回答可能）
            }}
            onTimeout={() => {
              // 「回答」ボタンを押下していない場合のみ未回答データを送信
              // 既に回答を送信済みの場合（submittedQuestions に含まれる場合）は何もしない
              if (!submittedQuestions.has(question.id)) {
                submitUnanswered(question.id);
              }
              stopTimer();
            }}
            results={results}
            isArchive={false}
            onTimerTick={() => {
              // TestQuestion内部でタイマーを管理するため、ここでは何もしない
            }}
            // scheduledCloseTimeがあれば、statusに関わらず締切時刻を渡す
            // （リロード時にstatusが正しく復元されていない可能性を考慮）
            // 残り時間は TestTimer が毎tick「締切時刻 − serverNow()」の差分で計算する
            // （開始時に秒へ切り捨てて渡すと最大1秒進んだ表示になるため、締切時刻をそのまま渡す）
            deadlineMs={scheduledCloseTimesRef.current[question.id]}
            isExplaining={isExplaining}
            isTrialUser={isTrialUser}
            lectureId={lectureIdStr ?? undefined}
            userId={userId}
          />
        </div>
      );
    });
  }, [questionsData, selectedChoices, submittedQuestions, answerSendStates, selectChoice, submitAnswer, submitUnanswered, stopTimer, filterQuestions, isInPiP, isPiPSupported, handleTogglePiP, questionAggregations, questionNumMap, displayNumberMap, isTrialUser, lectureIdStr, userId]);

  // チャット入力をレンダリングする共通関数
  const renderChatInput = useCallback((inputId?: string, buttonId?: string) => {
    return (
      <ChatInput
        isEnabled={!isArchive && isInputEnabled}
        maxLength={200}
        onSubmit={handleChatSubmit}
        placeholder={chatInputPlaceholder}
        inputId={inputId}
        buttonId={buttonId}
      />
    );
  }, [isArchive, isInputEnabled, handleChatSubmit, chatInputPlaceholder]);

  // 資料をレンダリングする共通関数
  const renderResources = useCallback(() => {
    if (visibleMaterials.length === 0) {
      return <p className="no-materials">資料はありません</p>;
    }
    return (
      <div className="resource-list">
        {visibleMaterials.map((material) => {
          const fileExtension = material.filename.split('.').pop()?.toUpperCase() || '';
          const fileSizeMB = (material.file_size / (1024 * 1024)).toFixed(1);
          return (
            <div key={material.id} className="resource-item">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="resource-title">{material.original_filename}</div>
                <div className="resource-meta">
                  {getMaterialDistributionLabel(material.distribution_type)}・{fileExtension}・{fileSizeMB}MB
                </div>
                {material.description && <div className="resource-desc">{material.description}</div>}
              </div>
              <a href={material.download_full_url} download={material.original_filename} className="resource-link">
                ダウンロード
              </a>
            </div>
          );
        })}
      </div>
    );
  }, [visibleMaterials]);

  // 注意事項をレンダリングする共通関数
  const renderNotices = useCallback(() => {
    const raw = noticeData?.notice;
    const html = typeof raw === 'string' ? raw.trim() : '';
    if (!html) {
      return <p className="no-notice">注意事項はありません</p>;
    }
    return (
      <div
        className="notice-html-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }, [noticeData]);

  // 概要をレンダリングする共通関数
  const formatArchiveDuration = useCallback((seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  const renderOverview = useCallback(() => {
    const dateFormat: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };
    return (
      <>
        {lectureData?.name && <h3 className="overview-title">{lectureData.name}</h3>}
        {lectureData?.description && (
          <div className="overview-description">{lectureData.description}</div>
        )}
        {((lectureData?.speakers?.length) || lectureData?.live?.started_at || lectureData?.archive?.actual_started_at) && (
          <div className="overview-meta">
            {lectureData?.speakers && lectureData.speakers.length > 0 && (
              <div>講師: {lectureData.speakers.map((s) => s.name).join('、')}</div>
            )}
            {isArchive ? (
              <>
                {lectureData?.archive?.actual_started_at && (
                  <div>
                    録画日時: {new Date(lectureData.archive.actual_started_at).toLocaleString('ja-JP', dateFormat)}
                  </div>
                )}
                {lectureData?.archive?.duration != null && (
                  <div>再生時間: {formatArchiveDuration(lectureData.archive.duration)}</div>
                )}
              </>
            ) : (
              <>
                {lectureData?.live?.started_at && (
                  <div>
                    開始: {new Date(lectureData.live.started_at).toLocaleString('ja-JP', dateFormat)}
                  </div>
                )}
                {lectureData?.live?.ended_at && (
                  <div>
                    終了: {new Date(lectureData.live.ended_at).toLocaleString('ja-JP', dateFormat)}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </>
    );
  }, [lectureData, isArchive, formatArchiveDuration]);

  // チャプターをレンダリングする共通関数
  const renderChapters = useCallback(() => {
    if (!lectureData?.chapters || lectureData.chapters.length === 0) {
      return <p className="muted">チャプターはありません</p>;
    }
    return (
      [...lectureData.chapters]
        .sort((a, b) => {
          if (a.sort_key !== undefined && b.sort_key !== undefined) {
            return a.sort_key - b.sort_key;
          }
          return a.start_time - b.start_time;
        })
        .map((chapter) => {
          const timestampStr = formatTimestamp(chapter.start_time);
          return (
            <div
              key={chapter.id}
              className="chapter-item"
              role="button"
              tabIndex={0}
              aria-label={`${timestampStr} ${chapter.title}`}
              onClick={() => handleChapterClick(chapter.start_time)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChapterClick(chapter.start_time);
                }
              }}
            >
              <div className="chapter-item-title">{chapter.title}</div>
              <div className="chapter-item-meta">{timestampStr}</div>
              {chapter.description && <div className="chapter-item-desc">{chapter.description}</div>}
            </div>
          );
        })
    );
  }, [lectureData?.chapters, formatTimestamp, handleChapterClick]);

  // メッセージをレンダリングする共通関数（ライブのみ）
  const renderMessage = useCallback(() => {
    const msg = lectureData?.message;
    if (!msg || !hasVisibleLectureMessage(msg, isArchive)) return null;

    const title = msg.title?.trim() ?? '';
    const rawContents = msg.contents?.trim() ?? '';
    const visibleContents = stripHtmlForMessageCheck(rawContents);

    return (
      <div className="lecture-message" id={isSp ? "sp-lecture-message" : "lecture-message"}>
        <div className="lecture-message-content">
          <div className="lecture-message-icon">
            <Megaphone className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="lecture-message-text">
            {title ? <p className="lecture-message-title">{title}</p> : null}
            {visibleContents ? (
              <div
                className="lecture-message-body notice-html-content"
                dangerouslySetInnerHTML={{ __html: rawContents }}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }, [lectureData?.message, isSp, isArchive]);

  // アンケートリンクをレンダリングする共通関数（試用ユーザーの場合は非表示）
  const renderSurvey = useCallback(() => {
    // ライブ中は動画下アンケートを非表示（講義終了後は結果サマリー内に表示）
    if (isTrialUser || !lectureData?.survey_url || !isArchive) return null;
    return (
      <div className="survey-link-container" id="survey-link-container" aria-label="アンケート">
        <div className="survey-link-text">
          <span className="survey-link-title">アンケートにご協力お願いします</span>
          <span className="survey-note">講義後1週間受付</span>
        </div>
        <a href={lectureData.survey_url} target="_blank" rel="noopener noreferrer" className="survey-link-cta">
          アンケートに回答する
        </a>
      </div>
    );
  }, [lectureData, isTrialUser, isArchive]);

  /** ライブ時のみ使用。SP/PC で同時に2つマウントしない（表示中レイアウトの1か所だけ） */
  const renderLiveIvsPlayer = useCallback(() => {
    return (
      <IVSPlayer
        ref={ivsPlayerRef}
        m3u8Url={playbackUrl}
        {...(lectureData?.live && { lectureLiveStatus: lectureData.live.status })}
        autoplay={false}
        onStateChange={() => {}}
        onError={(err) => {
          console.error('[IVSPlayer] Error:', err);
        }}
      />
    );
  }, [playbackUrl, lectureData?.live]);

  // PiP event listeners
  useEffect(() => {
    const video = ivsPlayerRef.current?.getVideoElement();
    if (!video || !isPiPSupported) return;

    const handleEnterPiP = () => {
      setIsInPiP(true);
      if (pipBtnRef.current) {
        pipBtnRef.current.setAttribute('aria-pressed', 'true');
        pipBtnRef.current.textContent = 'PiP終了';
        pipBtnRef.current.title = 'PiPを終了';
      }
    };

    const handleLeavePiP = () => {
      setIsInPiP(false);
      if (pipBtnRef.current) {
        pipBtnRef.current.setAttribute('aria-pressed', 'false');
        pipBtnRef.current.textContent = 'PiP';
        pipBtnRef.current.title = 'ピクチャインピクチャ';
      }
    };

    video.addEventListener('enterpictureinpicture', handleEnterPiP);
    video.addEventListener('leavepictureinpicture', handleLeavePiP);

    return () => {
      video.removeEventListener('enterpictureinpicture', handleEnterPiP);
      video.removeEventListener('leavepictureinpicture', handleLeavePiP);
    };
  }, [isPiPSupported]);

  // 認証中（idle / loading）はローディング表示
  if (authStatus === 'idle' || authStatus === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#009688]"></div>
          <p>認証中...</p>
        </div>
      </div>
    );
  }

  // 認証エラー
  // Manabun への復帰導線（リンク）を表示する（STU-13対応）。
  // ドメイン設定（frontend-settings）が未取得の間はリンクを表示しない
  // （設定取得はバックオフ付きで自動リトライされ、取得後の再レンダーで表示される）
  if (authStatus === 'error') {
    // トークンがない場合（Manabun経由でない直接アクセス）
    if (authError?.type === 'no_token') {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <p className="mb-2 text-gray-700">アクセスできません</p>
            <p className="text-sm text-gray-500">Manabunから正しく遷移してください。</p>
            {manabunDomain && (
              <a
                href={manabunBackUrl}
                target="_self"
                className="mt-4 inline-block rounded bg-[#009688] px-4 py-2 text-white"
              >
                Manabunに戻る
              </a>
            )}
          </div>
        </div>
      );
    }
    // トークン期限切れ・その他の認証エラー
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-2 text-gray-700">認証エラーが発生しました</p>
          <p className="mt-2 text-sm text-gray-500">Manabunに戻り、再度ログインを行ってください。</p>
          {manabunDomain && (
            <a
              href={manabunBackUrl}
              target="_self"
              className="mt-4 inline-block rounded bg-[#009688] px-4 py-2 text-white"
            >
              Manabunに戻る
            </a>
          )}
        </div>
      </div>
    );
  }

  // Loading state（初回ロードのみ。STU-H1対応）
  // 講義終了イベント等で走るバックグラウンド再フェッチ（fetchAllData）でも
  // isLoading は true になるが、その間に視聴画面全体をスピナーへ置き換えると
  // 映像・チャットが一瞬消えて視聴体験を損なうため、lectureData 取得済みなら表示を維持する
  if (!lectureData && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#009688]"></div>
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  // Error state（初回ロード失敗のみ。STU-H1対応）
  // バックグラウンド再フェッチの失敗で視聴画面全体をエラー画面に置き換えない
  // （表示済みのデータで視聴を継続できる。次の講義ステータス変化等で再取得される）
  // 再試行ボタンはエラークリアに加えて初回フェッチを再実行する（STU-2対応）。
  // 従来はエラークリアのみで再フェッチされず、無限スピナーになっていた
  if (!lectureData && error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-red-600">エラーが発生しました</p>
          <p className="text-sm text-gray-600">{error.message}</p>
          <button
            type="button"
            onClick={() => {
              clearError();
              runInitialFetch();
            }}
            className="mt-4 rounded bg-[#009688] px-4 py-2 text-white"
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  // リハーサル講義のアクセスチェック: 社員ユーザーのみアクセス可能
  // 認証が完了している場合のみチェック（認証中は待機）
  if (
    lectureData?.is_rehearsaled &&
    authStatus === 'authenticated' &&
    userType !== 'staff'
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">ページが見つかりません</p>
        </div>
      </div>
    );
  }

  // 講座へのアクセス権チェック: USER.COURSES配列のCOURSE_KOZA_ID、COURSE_YEAR、COURSE_MONTHと照合
  // lecture.jsonのcourse.idとcourse.year_months配列と照合
  // 認証が完了し、lectureDataが読み込まれている場合のみチェック
  if (
    authStatus === 'authenticated' &&
    manabunUser &&
    lectureData?.course?.id !== undefined &&
    lectureData?.course?.year_months &&
    lectureData.course.year_months.length > 0
  ) {
    const hasAccess = canAccessCourse(
      lectureData.course.id,
      lectureData.course.year_months
    );

    if (!hasAccess) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">ページが見つかりません</p>
          </div>
        </div>
      );
    }
  }

  const spTheaterToggleButton = (
    <button
      id="sp-theater-toggle-btn-sp"
      className="sp-theater-toggle-btn"
      type="button"
      aria-label="シアターモード"
      aria-pressed={isTheaterMode}
      title="シアターモード"
      onClick={toggleTheaterMode}
    >
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
      </svg>
    </button>
  );

  return (
    <>
      {/* エラーメッセージモーダル */}
      {errorMessage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="error-modal-title"
          onClick={() => setErrorMessage(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* エラーアイコン */}
            <div
              style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 20px',
                backgroundColor: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            {/* エラーメッセージ */}
            <h2
              id="error-modal-title"
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '12px',
              }}
            >
              エラー
            </h2>
            <p
              style={{
                fontSize: '16px',
                color: '#4b5563',
                marginBottom: '24px',
                lineHeight: '1.6',
              }}
            >
              {errorMessage}
            </p>

            {/* OKボタン */}
            <button
              type="button"
              onClick={() => setErrorMessage(null)}
              style={{
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                minWidth: '120px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ef4444';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Header（SP時は講義名をCSSで非表示） */}
      <header className="header" role="banner">
        <div className="header-content">
          <div className="header-left">
            <div className="brand">
              <img className="logo-img" src={logoImg} alt="eライブスタディ" />
            </div>
            <div className="header-lecture-title" id="lecture-title-text" aria-label="講義タイトル">
              {/* リハーサル講義の場合は本番配信と見分けが付くようバッジを表示 */}
              {lectureData?.is_rehearsaled && (
                <span className="rehearsal-badge">リハーサル</span>
              )}
              {lectureData?.name || ''}
            </div>
          </div>
          <nav aria-label="メインナビ">
            <ul className="nav-menu">
              <li className="viewer-mode-controls" id="viewer-mode-controls" aria-label="表示モード切替">
                {/* リアルタイム接続断の通知バッジ（一度接続成功した後の切断時のみ表示） */}
                {showRealtimeDisconnectedBadge && (
                  <span className="realtime-disconnected-badge" role="status">
                    ネットワーク接続 再接続中
                  </span>
                )}
                <button
                  className="viewer-mode-btn theater-mode-btn"
                  id="theater-mode-btn"
                  type="button"
                  title="シアターモード"
                  aria-pressed={isTheaterMode}
                  onClick={toggleTheaterMode}
                >
                  シアターモード
                </button>
                <button
                  className="viewer-mode-btn toggle-tabs-btn"
                  id="toggle-tabs-btn"
                  type="button"
                  title="サイドバーの表示/非表示"
                  aria-pressed={!isSidebarVisible}
                  onClick={toggleSidebar}
                >
                  {isSidebarVisible ? 'サイドバー非表示' : 'サイドバー表示'}
                </button>
              </li>
              {/* SP用: リアルタイム接続断バッジ（PCでは表示モード切替内に表示するためCSSで非表示） */}
              {showRealtimeDisconnectedBadge && (
                <li className="realtime-disconnected-badge-sp">
                  <span className="realtime-disconnected-badge" role="status">
                    ネットワーク接続 再接続中
                  </span>
                </li>
              )}
              <li>
                <a 
                  className="back-button" 
                  href={manabunBackUrl}
                  target="_self"
                >
                  戻る
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="viewer-container" role="main">
        {/* Theater mode title header（シアター時のみ表示・PC用） */}
        <div className="lecture-title-header" id="theater-lecture-title-header" aria-hidden={!isTheaterMode || isSp}>
          <h1 className="lecture-title-text" id="theater-lecture-title-text">
            {/* リハーサル講義の場合は本番配信と見分けが付くようバッジを表示 */}
            {lectureData?.is_rehearsaled && (
              <span className="rehearsal-badge">リハーサル</span>
            )}
            {lectureData?.name || ''}
          </h1>
          <div className="title-controls" id="theater-title-controls" aria-label="表示モード切替">
            <button
              className="viewer-mode-btn theater-mode-btn"
              id="theater-header-theater-mode-btn"
              type="button"
              title="通常画面に戻る"
              aria-pressed={isTheaterMode}
              onClick={toggleTheaterMode}
            >
              通常画面に戻る
            </button>
            <button
              className="viewer-mode-btn toggle-tabs-btn"
              id="theater-header-toggle-tabs-btn"
              type="button"
              title="サイドバーの表示/非表示"
              aria-pressed={!isSidebarVisible}
              onClick={toggleSidebar}
            >
              {isSidebarVisible ? 'サイドバー非表示' : 'サイドバー表示'}
            </button>
          </div>
        </div>

        {/* EQ Player の #eqplayer div は React 管理外で命令的に生成する（Step2 useEffect）。
            React Reconciliation の干渉を避けるため JSX には置かない。 */}

        {/* SP専用 - display:none で切り替え（DOM維持） */}
        <div style={{ display: isSp ? undefined : 'none' }}>
          {/* SP シアターシェル: 横シアター時は CSS でフルスクリーン 左右分割レイアウトになる */}
          <div className="sp-theater-shell">
            {/* 左カラム: 動画エリア */}
            <div className="sp-theater-left">
              <div className="sp-sticky-shell" id="sp-sticky-shell" ref={spStickyShellRef} aria-hidden="false">
                <div className="sp-sticky-shell-inner">
                  <div className="video-wrapper" id="sp-video-wrapper" ref={spVideoWrapperRef} aria-label="動画">
                    {!isArchive && spTheaterToggleButton}
                    {isArchive ? (
                      <div className="archive-loading-placeholder flex h-full items-center justify-center bg-black text-white text-sm">
                        <p>アーカイブ動画を読み込み中です</p>
                      </div>
                    ) : isSp ? (
                      renderLiveIvsPlayer()
                    ) : null}
                    <noscript>
                      <div>このコンテンツを見るためにはJavaScriptを有効にしてください。</div>
                    </noscript>
                  </div>
                  {renderMessage()}
                  {renderSurvey()}
                </div>
                </div>
              </div>

              {/* 右カラム: テスト/チャット タブ（横シアター時のみ CSS で表示） */}
              <div className="sp-theater-right">
                <div className="sp-theater-right-tabs" role="tablist" aria-label="シアターモードタブ">
                  <button
                    className="sp-theater-right-tab-btn"
                    type="button"
                    role="tab"
                    aria-selected={spTheaterRightTab === 'test'}
                    onClick={() => {
                      setSpTheaterRightTab('test');
                      setActiveSpTab('test');
                    }}
                  >
                    テスト
                  </button>
                  <button
                    className="sp-theater-right-tab-btn"
                    type="button"
                    role="tab"
                    aria-selected={spTheaterRightTab === 'chat'}
                    onClick={() => {
                      setSpTheaterRightTab('chat');
                      setActiveSpTab('chat');
                    }}
                  >
                    チャット
                  </button>
                </div>
                <div className="sp-theater-right-body">
                  <div className="sp-content" id="sp-content" aria-label="SPコンテンツ">
              <div className="sp-pane" id="sp-pane-overview" role="tabpanel" aria-label="概要">
                <>
                    <section className="panel" id="sp-lecture-overview-section" aria-label="概要">
                      <h2 className="panel-title">概要</h2>
                      <div className="panel-body">
                        {renderOverview()}
                      </div>
                    </section>
                    {!isTrialUser && (
                      <section className="panel" id="sp-resources-section" aria-label="資料">
                        <h2 className="panel-title">資料</h2>
                        <div className="panel-body">
                          {renderResources()}
                        </div>
                      </section>
                    )}
                    <section className="panel" id="sp-notices-section" aria-label="注意事項">
                      <h2 className="panel-title">注意事項</h2>
                      <div className="panel-body">
                        {renderNotices()}
                      </div>
                    </section>
                </>
              </div>
              <div className="sp-pane" id="sp-pane-chat" role="tabpanel" aria-label="チャット">
                <section className="panel sp-pane-panel" aria-label="チャット">
                    <div className="sp-pane-body" id="sp-pane-chat-body">
                      <div className="chat-shell">
                        <ChatMessages
                          messages={visibleChatMessages}
                          isLoading={useRealtime && isChatHistoryLoading}
                          errorMessage={useRealtime ? chatConnectionError : null}
                        />
                        {renderChatInput()}
                      </div>
                    </div>
                  </section>
              </div>
              {/* SP: テストタブ用ペイン（モック viewer.js: テスト未実施時は待機カードの補足だけ更新） */}
              <div className="sp-pane" id="sp-pane-test" role="tabpanel" aria-label="テスト">
                <section className="panel sp-pane-panel" aria-label="テスト">
                  <div className="sp-pane-body" id="sp-pane-test-body">
                    {questionsData?.questions && questionsData.questions.length > 0 ? (
                      <>
                        {(() => {
                          const activeQuestions = questionsData.questions.filter(
                            (q) => q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied',
                          );
                          if (activeQuestions.length === 0 && !isLectureEnded) {
                            return (
                              <div id="test-section" className="test-section" aria-label="テスト本体">
                                <div className="test-waiting-card" id="test-waiting-card" role="status" aria-live="polite">
                                  <div className="test-waiting-title">テストは現在実施しておりません。</div>
                                  <div className="test-waiting-sub">
                                    {isArchive ? 'この講義のテスト結果を表示します。' : '開始されると自動でこの画面が開きます。'}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          // 講義終了時は問題表示をスキップして結果サマリーのみ表示
                          if (isLectureEnded) {
                            return (
                              <>
                                {lectureData && questionsData && (
                                  <TestSummary
                                    lectureData={lectureData}
                                    questions={questionsData.questions}
                                    selectedChoices={selectedChoices}
                                    questionAggregations={questionAggregations}
                                    isTrialUser={isTrialUser}
                                    isArchive={isArchive}
                                    userRanking={userRanking}
                                    {...(userId !== undefined && { userId })}
                                    {...(lectureIdStr !== null && { lectureId: lectureIdStr })}
                                  />
                                )}
                              </>
                            );
                          }
                          return (
                            <>
                              {renderTestQuestions(false)}
                              {isLectureEnded && lectureData && questionsData && (
                                <TestSummary
                                  lectureData={lectureData}
                                  questions={questionsData.questions}
                                  selectedChoices={selectedChoices}
                                  questionAggregations={questionAggregations}
                                  isTrialUser={isTrialUser}
                                  isArchive={isArchive}
                                  userRanking={userRanking}
                                  {...(userId !== undefined && { userId })}
                                  {...(lectureIdStr !== null && { lectureId: lectureIdStr })}
                                />
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div id="test-section" className="test-section" aria-label="テスト本体">
                        <div className="test-waiting-card" id="test-waiting-card" role="status" aria-live="polite">
                          <div className="test-waiting-title">テストは現在実施しておりません。</div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
              {isArchive && (
                <div className="sp-pane" id="sp-pane-chapters" role="tabpanel" aria-label="チャプター">
                  <section className="panel sp-pane-panel" aria-label="チャプター">
                      <div className="sp-pane-body" id="sp-pane-chapters-body">
                        <div className="chapter-shell">
                          <div className="chapter-list" aria-label="チャプター一覧">
                            {renderChapters()}
                          </div>
                        </div>
                      </div>
                    </section>
                </div>
              )}
                  </div>{/* /sp-content */}
                </div>{/* /sp-theater-right-body */}
              </div>{/* /sp-theater-right */}
            </div>{/* /sp-theater-shell */}
        </div>{/* /SP専用 */}

        {/* PC専用 - display:none で切り替え（DOM維持） */}
        <div className="viewer-grid" id="live-grid" style={{ display: !isSp ? undefined : 'none' }}>
          {/* Main Column */}
          <section className="viewer-main-col" id="live-main-col" aria-label="メイン">
            {/* Video Panel */}
            <div
              id="main-video"
              className="main-tab-pane active"
              role="tabpanel"
              aria-label="動画"
            >
              <div className="video-wrapper" id="video-wrapper" ref={videoWrapperRef} aria-label="動画">
                {/* SP: シアターボタンは動画上 */}
                <button
                  id="sp-theater-toggle-btn"
                  className="sp-theater-toggle-btn"
                  type="button"
                  aria-label="シアターモード"
                  aria-pressed={isTheaterMode}
                  title="シアターモード"
                  onClick={toggleTheaterMode}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
                  </svg>
                </button>

                {/* PiP Button (Live only) */}
                {!isArchive && isPiPSupported && !hasTestInProgress && (
                  <button
                    id="pip-toggle-btn"
                    ref={pipBtnRef}
                    className="pip-toggle-btn"
                    type="button"
                    aria-label="ピクチャインピクチャ"
                    aria-pressed={isInPiP}
                    title="ピクチャインピクチャ"
                    onClick={handleTogglePiP}
                  >
                    PiP
                  </button>
                )}

                {/* アーカイブ時は #eqplayer を Step3 useEffect でこのラッパーへ移動する。
                ライブ時は isSp でないときのみ IVSPlayer（SP/PC 同時マウントを避ける） */}
                {!isArchive && !isSp && renderLiveIvsPlayer()}

                <noscript>
                  <div>このコンテンツを見るためにはJavaScriptを有効にしてください。Please enable JavaScript to watch this content.</div>
                </noscript>
              </div>

              {/* メッセージ（ライブ時） */}
              {renderMessage()}

              {/* メッセージ下: テスト中も動画をPiPで表示できる旨とボタン（ライブ時・テストあり・テスト中は非表示） */}
              {!isArchive && isPiPSupported && questionsData?.questions && questionsData.questions.length > 0 && !hasTestInProgress && (
                <div className="test-pip-controls test-pip-controls-below-message">
                  <div className="test-pip-hint">テスト中も動画を見ながら解答できます</div>
                  <div className="test-pip-actions">
                    <button
                      type="button"
                      className="test-pip-btn"
                      aria-label="動画をPiPで表示"
                      aria-pressed={isInPiP}
                      title="動画をPiPで表示"
                      onClick={handleTogglePiP}
                      disabled={!isPiPSupported}
                    >
                      {isInPiP ? 'PiP終了' : '動画をPiPで表示'}
                    </button>
                  </div>
                </div>
              )}

            </div>

            {/* アーカイブ: 動画とテスト結果の間にアンケート帯（PC） */}
            {renderSurvey()}

            {/* Test Section - #main-video の兄弟要素として配置 */}
            <section id="main-test" className="panel" aria-label="テスト">
              {questionsData?.questions && questionsData.questions.length > 0 ? (
                <>
                  {(() => {
                    const activeQuestions = questionsData.questions.filter(
                      (q) => q.status === 'answering' || q.status === 'tallying' || q.status === 'tallied',
                    );
                    if (activeQuestions.length === 0 && !isLectureEnded) {
                      return (
                        <div id="test-section" className="test-section" aria-label="テスト本体">
                          <div className="test-waiting-card" id="test-waiting-card" role="status" aria-live="polite">
                            <div className="test-waiting-title">テストは現在実施しておりません。</div>
                            <div className="test-waiting-sub">
                              {isArchive ? 'この講義のテスト結果を表示します。' : '開始されると自動でこの画面が開きます。'}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // 講義終了時は問題表示をスキップして結果サマリーのみ表示
                    if (isLectureEnded) {
                      return (
                        <>
                          {lectureData && questionsData && (
                            <TestSummary
                              lectureData={lectureData}
                              questions={questionsData.questions}
                              selectedChoices={selectedChoices}
                              questionAggregations={questionAggregations}
                              isTrialUser={isTrialUser}
                              isArchive={isArchive}
                              userRanking={userRanking}
                              {...(userId !== undefined && { userId })}
                              {...(lectureIdStr !== null && { lectureId: lectureIdStr })}
                            />
                          )}
                        </>
                      );
                    }
                    return (
                      <>
                        {renderTestQuestions(true)}
                        {isLectureEnded && lectureData && questionsData && (
                          <TestSummary
                            lectureData={lectureData}
                            questions={questionsData.questions}
                            selectedChoices={selectedChoices}
                            questionAggregations={questionAggregations}
                          isTrialUser={isTrialUser}
                          isArchive={isArchive}
                          userRanking={userRanking}
                          {...(userId !== undefined && { userId })}
                          {...(lectureIdStr !== null && { lectureId: lectureIdStr })}
                        />
                      )}
                    </>
                  );
                })()}
              </>
            ) : (
              <div id="test-section" className="test-section" aria-label="テスト本体">
                <div className="test-waiting-card" id="test-waiting-card" role="status" aria-live="polite">
                    <div className="test-waiting-title">テストは現在実施しておりません。</div>
                  </div>
                </div>
              )}
            </section>

            {/* Overview Section */}
            <section className="panel" id="lecture-overview-section" aria-label="概要">
              <h2 className="panel-title">概要</h2>
              <div className="panel-body">
                {renderOverview()}
              </div>
            </section>

            {/* Resources Section */}
            {!isTrialUser && (
              <section className="panel" id="resources-section-main" aria-label="資料">
                <h2 className="panel-title">資料</h2>
                <div className="panel-body">
                  {renderResources()}
                </div>
              </section>
            )}

            {/* Notices Section */}
            <section className="panel" id="notices-section" aria-label="注意事項">
              <h2 className="panel-title">注意事項</h2>
              <div className="panel-body">
                {renderNotices()}
              </div>
            </section>
          </section>

          {/* Sidebar */}
          <aside className="viewer-side-col" id="live-side-col" aria-label="サイドバー">
            <section className="panel sidebar-panel" aria-label={isArchive ? 'チャット・チャプター' : 'チャット'}>
              {/* Side Tab Navigation */}
              <nav className="side-tab-nav" aria-label="サイドバータブ">
                <button
                  className={`side-tab-button ${activeSideTab === 'side-chat' ? 'active' : ''}`}
                  type="button"
                  data-side-tab="side-chat"
                  aria-selected={activeSideTab === 'side-chat'}
                  onClick={() => handleSideTabClick('side-chat')}
                >
                  チャット
                </button>
                {isArchive && (
                  <button
                    className={`side-tab-button ${activeSideTab === 'side-chapters' ? 'active' : ''}`}
                    type="button"
                    data-side-tab="side-chapters"
                    aria-selected={activeSideTab === 'side-chapters'}
                    onClick={() => handleSideTabClick('side-chapters')}
                  >
                    チャプター
                  </button>
                )}
              </nav>

              <div className="side-tab-content">
                {/* Chat Tab */}
                <div
                  id="side-chat"
                  className={`side-tab-pane ${activeSideTab === 'side-chat' ? 'active' : ''}`}
                  role="tabpanel"
                  aria-label="チャット"
                >
                  <div className="chat-shell">
                    <ChatMessages
                      containerId="chat-messages"
                      messages={visibleChatMessages}
                      isLoading={useRealtime && isChatHistoryLoading}
                      errorMessage={useRealtime ? chatConnectionError : null}
                    />
                    {renderChatInput('chat-input', 'chat-send')}
                  </div>
                </div>

                {/* Chapters Tab (Archive only) */}
                {isArchive && (
                  <div
                    id="side-chapters"
                    className={`side-tab-pane ${activeSideTab === 'side-chapters' ? 'active' : ''}`}
                    role="tabpanel"
                    aria-label="チャプター"
                  >
                    <div className="chapter-shell">
                      <div className="chapter-title">チャプター</div>
                      <div className="chapter-list" id="chapter-list-tab" aria-label="チャプター一覧">
                        {renderChapters()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </aside>
        </div>
      </main>

      {/* SP Footer Menu (Mobile) - 順序: 概要 → テスト → チャット（添付モック画像に合わせる） */}
      <nav className="sp-footer-menu" id="sp-footer-menu" aria-label="SPメニュー" role="tablist">
        <button
          className={`sp-footer-btn ${activeSpTab === 'overview' ? 'active' : ''}`}
          type="button"
          data-sp-tab="overview"
          role="tab"
          aria-controls="lecture-overview-section"
          aria-selected={activeSpTab === 'overview'}
          onClick={() => handleSpTabClick('overview')}
        >
          <Info className="sp-footer-icon" size={22} aria-hidden="true" />
          <span className="sp-footer-label">概要</span>
        </button>
        <button
          className={`sp-footer-btn ${activeSpTab === 'test' ? 'active' : ''}`}
          type="button"
          data-sp-tab="test"
          role="tab"
          aria-controls="sp-pane-test"
          aria-selected={activeSpTab === 'test'}
          onClick={() => handleSpTabClick('test')}
        >
          <ClipboardCheck className="sp-footer-icon" size={22} aria-hidden="true" />
          <span className="sp-footer-label">テスト</span>
        </button>
        <button
          className={`sp-footer-btn ${activeSpTab === 'chat' ? 'active' : ''}`}
          type="button"
          data-sp-tab="chat"
          role="tab"
          aria-controls="side-chat"
          aria-selected={activeSpTab === 'chat'}
          onClick={() => handleSpTabClick('chat')}
        >
          <MessageCircle className="sp-footer-icon" size={22} aria-hidden="true" />
          <span className="sp-footer-label">チャット</span>
        </button>
        {isArchive && (
          <button
            className={`sp-footer-btn ${activeSpTab === 'chapters' ? 'active' : ''}`}
            type="button"
            data-sp-tab="chapters"
            role="tab"
            aria-controls="side-chapters"
            aria-selected={activeSpTab === 'chapters'}
            onClick={() => handleSpTabClick('chapters')}
          >
            <svg className="sp-footer-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M3.75 6.75A.75.75 0 0 1 4.5 6h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25A.75.75 0 0 1 4.5 11.25h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25A.75.75 0 0 1 4.5 16.5h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
            </svg>
            <span className="sp-footer-label">チャプター</span>
          </button>
        )}
      </nav>

      {/* Footer */}
      <footer className="viewer-footer" role="contentinfo" aria-label="サイトフッター">
        <div className="viewer-footer-inner">
          <img className="viewer-footer-logo" src={logoImg} alt="Foresight" />
          <div className="viewer-footer-copy">Copyright© Foresight All rights reserved.</div>
        </div>
      </footer>

      {/* SPアーカイブ: #eqplayer(body直下)より前面に出すため body へ portal */}
      {isSp && isArchive && createPortal(spTheaterToggleButton, document.body)}
    </>
  );
}

export default LecturePage;
